import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { errorText, contactExists } from '../lib/apiErrors';
import { createEventContactsApi, isEventContactConflict } from '../lib/eventContactsApi';
import { eventEnrollmentOutcomeMessages } from '../lib/eventEnrollmentOutcomes';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { FORMER_MEMBER, personLabel, teammateLabels } from '../lib/teammateLabels';
import { copyToClipboard } from '../lib/clipboard';
import { newIdempotencyKey } from '../components/list-management/lmApi';
import LeadPipelineCard from '../components/leads/LeadPipelineCard';
import axios from 'axios';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import {
    ChevronLeft, Mail, Phone, Building2, Edit3, Activity,
    Clock, User, History, Briefcase, ExternalLink, X, Check,
    Linkedin, FileText, Users, Sparkles, Globe, Loader2, ChevronDown,
    GraduationCap, Award, Search, Calendar, Workflow, Camera, Database
} from 'lucide-react';
import { Button } from '../components/ui/button';
import EnrollCampaignModal from '../components/EnrollCampaignModal';
import ActivityCard from '../components/activities/ActivityCard';
import TaskAssignment from '../components/activities/TaskAssignment';
import TaskStateControls from '../components/activities/TaskStateControls';
import { activityDisplay } from '../utils/activityTitle';
import { formatDateInZone, formatInZone, formatRelativeInZone } from '../lib/timezone';
import useDisplayZone from '../hooks/useDisplayZone';
import DISCBadge from '../components/DISCBadge';
import DISCEditor from '../components/DISCEditor';
import CustomFieldRenderer from '../components/CustomFieldRenderer';
import CustomFieldBadge from '../components/custom-fields/CustomFieldBadge';
import { missingRequiredCustomFields } from '../components/custom-fields/requiredFields';
import useCustomFields from '../hooks/useCustomFields';
import { useCommunicationDetail } from '../hooks/useCommunicationDetail';
import { isCommunicationEmailRow } from '../utils/communicationRow';
import { isValidEmail } from '../utils/emailValidation';
import { normalizeContactPayload } from '../utils/normalizeContactPayload';
import CampaignProgressTracker from '../components/CampaignProgressTracker';
import TagInput from '../components/TagInput';
import FollowUpPrompt from '../components/FollowUpPrompt';
import VoiceButton from '../components/ui/VoiceButton';
import MdFilePanel from '../components/MdFilePanel';
import PitchBookRecordTab from '../components/pitchbook/PitchBookRecordTab';
import { KEY_ROLE_LABELS } from '../components/table/CellRenderers';
import ContactEventPhotos from '../components/events/ContactEventPhotos';
import MeetingActionModal from '../components/MeetingActionModal';
import { useScheduleCapabilities, SCHEDULE_DISABLED_COPY } from '../hooks/useScheduleCapabilities';
import ContactEmailComposer from '../components/ContactEmailComposer';
import { isFinalOutcome, logEventEmail, offerToCloseFollowUps, safeOperationId } from '../lib/eventOutreachActions';
import {
    CLOSE_REASONS, CLOSE_TARGET, eventHasStarted, statusTargetFields, statusTargetOptions,
} from '../lib/eventStatusTargets';
import { compareByDue, isTaskOpen } from '../lib/taskDue';
import { notifyTasksChanged, onTasksChanged } from '../lib/tasksApi';
import CompanyPicker from '../components/CompanyPicker';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { useDirtyFields } from '../hooks/useDirtyFields';
import usePipelineDefinition from '../hooks/usePipelineDefinition';
import ContactAttributionPanel from '../components/contacts/ContactAttributionPanel';
import ContactDealsCard from '../components/contacts/ContactDealsCard';
import ContactEventsTab from '../components/contacts/ContactEventsTab';
import { ContactCountStrip, ContactEventsRail, eventsCountLabel } from '../components/contacts/ContactEventsRail';
import useContactEvents from '../hooks/useContactEvents';
import { getContactHistory } from '../lib/historyApi';
import {
    actorDetails, historyChanges, historyContext, historyFieldLabel, historyItems as normalizeHistoryItems,
    historyKindLabel, historyOperationId, historyRelatedPeople, historySource, historySourceLabel, historyTimeLabel,
    historyValue, humanize as humanizeWords,
} from '../lib/historyDisplay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Canonical 8-value buying-role enum (matches backend BuyingRole). The
// legacy enum values ("Decision Maker", "User") are kept in the colour map
// so historic docs still render nicely while the server-side migration
// script backfills the new field.
const ROLES = [
    'Decision maker', 'Champion', 'Economic buyer', 'Influencer',
    'Technical buyer', 'End user', 'Blocker', 'Unknown',
];
const SENIORITY = ['C-suite', 'VP', 'Director', 'Manager', 'IC'];
const STRENGTHS = ['strong', 'moderate', 'weak', 'unknown'];
const ROLE_COLORS = {
    'Decision maker':  'bg-violet-100 text-violet-700',
    'Champion':        'bg-emerald-100 text-emerald-700',
    'Economic buyer':  'bg-violet-200 text-violet-800',
    'Influencer':      'bg-blue-100 text-blue-700',
    'Technical buyer': 'bg-cyan-100 text-cyan-700',
    'End user':        'bg-stone-100 text-stone-700',
    'Blocker':         'bg-red-100 text-red-700',
    'Unknown':         'bg-stone-100 text-stone-500',
    // Legacy labels for pre-migration contact docs:
    'Decision Maker':  'bg-violet-100 text-violet-700',
    'User':            'bg-stone-100 text-stone-700',
};
const STRENGTH_COLORS = { strong: 'text-emerald-600 bg-emerald-50', moderate: 'text-amber-600 bg-amber-50', weak: 'text-red-600 bg-red-50', unknown: 'text-stone-500 bg-stone-50' };
const FIELD_LABELS = {
    first_name: 'First Name', last_name: 'Last Name', email: 'Email', phone: 'Phone', mobile: 'Mobile',
    title: 'Job Title', department: 'Department', seniority: 'Seniority', linkedin_url: 'LinkedIn URL',
    role_in_deal: 'Role in Deal', buying_role: 'Buying Role',
    influence_score: 'Influence Score', engagement_score: 'Engagement Score',
    bio: 'Bio', tips_do: 'Coaching: Do', tips_dont: 'Coaching: Avoid',
    relationship_strength: 'Relationship', notes: 'Notes', tags: 'Tags', account_id: 'Company',
    // #864 (C1): the contact's owner, coach and credit by their product names,
    // and labels for the dedicated setters' keys that used to render raw.
    owner_id: 'Contact Owner',
    coach_assigned_user_id: 'Assigned Coach',
    referred_by: 'Referral credit',
    pipeline_stage: 'Pipeline stage',
    sales_call_outcome: 'Sales call outcome',
    lead_status: 'Lead status',
    source_tag: 'Source tag',
    status_event: 'Status event',
};

// #532 residue — Created / Last Updated / Change Log stamps render in the
// viewer's display zone WITH a zone label, not the browser's unlabelled clock.
// Offset-less API strings are UTC instants (shared parseInstant contract).
const formatDate = (ts, zone) => {
    if (!ts) return '--';
    return formatInZone(ts, { zone, formatStr: 'MM/dd/yyyy hh:mm a (zzz)' }) || String(ts);
};

function insightHasValue(value) {
    return value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0);
}


function formatInsightValue(value, { date = false, zone } = {}) {
    if (!insightHasValue(value)) return '—';
    if (date) {
        return formatDateInZone(value, { zone }) || String(value);
    }
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

function ContactInsightSection({ title, description, fields }) {
    const displayZone = useDisplayZone();
    const visible = fields.filter(f => insightHasValue(f.value));
    if (!visible.length) return null;
    return (
        <section className="overflow-hidden bg-white rounded-xl border border-stone-200">
                <div className="px-4 py-3 border-b border-stone-100 bg-stone-50/60">
                <h3 className="text-sm font-semibold text-stone-800" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h3>
                {description && <p className="text-xs text-stone-500 mt-0.5">{description}</p>}
            </div>
            <div className="grid grid-cols-1 gap-y-3 gap-x-8 p-4 md:grid-cols-2">
                {visible.map(f => (
                    <div key={f.label} className={f.full ? 'md:col-span-2' : ''}>
                        <div className="flex gap-3">
                            <span className="w-40 text-xs font-semibold tracking-wider uppercase text-stone-400 shrink-0">{f.label}</span>
                            {f.isLink ? (
                                <a href={String(f.value).startsWith('http') ? f.value : `https://${f.value}`} target="_blank" rel="noreferrer"
                                    className="text-sm text-blue-600 break-all hover:underline">
                                    {f.value}
                            </a>
                      ) : (
                                <span className="text-sm whitespace-pre-wrap break-words text-stone-700">{formatInsightValue(f.value, { ...f, zone: displayZone })}</span>
                            )}
                        </div>
        </div>
                ))}
            </div>
        </section>
    );
}

function ContactDataInsights({ contact }) {
    return (
        <div className="space-y-4">
            <div className="p-4 rounded-xl border bg-stone-50 border-stone-200">
                <div className="flex gap-3 items-start">
                    <Database className="w-5 h-5 text-stone-500 mt-0.5" />
                    <div>
                        <h3 className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Data Insights</h3>
                        <p className="mt-1 text-sm text-stone-600">Additional structured intelligence imported or enriched from trusted data sources. Relationship notes and coaching stay on About.</p>
                    </div>
                </div>
            </div>
            <ContactInsightSection title="Profile Details" fields={[
                { label: 'Prefix', value: contact.name_prefix },
                { label: 'Middle Name', value: contact.middle_name },
                { label: 'Gender', value: contact.gender },
                { label: 'Birth Year', value: contact.birth_year },
                { label: 'Fax', value: contact.fax },
            ]} />
            <ContactInsightSection title="Professional Context" fields={[
                { label: 'Primary Company', value: contact.primary_company_name },
                { label: 'Company Type', value: contact.primary_company_type },
                { label: 'Company Industry', value: contact.primary_company_industry },
                { label: 'Company Verticals', value: contact.primary_company_verticals },
                { label: 'Company Website', value: contact.primary_company_website, isLink: true },
                { label: 'Education', value: contact.education },
                { label: 'Biography', value: contact.bio, full: true },
            ]} />
            <ContactInsightSection title="Source Metadata" fields={[
                { label: 'Source', value: contact.source },
                { label: 'Source Tag', value: contact.source_tag },
                { label: 'Status Event', value: contact.status_event },
                { label: 'Created At', value: contact.created_at, date: true },
                { label: 'Updated At', value: contact.updated_at, date: true },
            ]} />
        </div>
    );
}

/* Reusable inline text field */
const InlineField = ({ label, value, field, editing, editForm, setEditForm, type, isFieldDirty }) => {
    const dirty = isFieldDirty && isFieldDirty(field);
    // #911: tags have their own read view (chips) below.
    if (!editing && type !== 'tags') return (
        <div>
            <p className="text-xs text-stone-400">{label}</p>
            <p className="text-sm font-medium break-words text-stone-800">{value || '--'}</p>
        </div>
    );
    if (type === 'select-seniority') return (
        <div>
            <label className="block text-xs text-stone-400 mb-0.5">{label}{dirty && <span className="text-amber-500 ml-0.5" title="Unsaved change">●</span>}</label>
            <select value={editForm[field] || ''} onChange={e => setEditForm(p => ({ ...p, [field]: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                <option value="">--</option>
                {SENIORITY.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
        </div>
    );
    if (type === 'select-role') return (
        <div>
            <label className="block text-xs text-stone-400 mb-0.5">{label}{dirty && <span className="text-amber-500 ml-0.5" title="Unsaved change">●</span>}</label>
            <select value={editForm[field] || ''} onChange={e => setEditForm(p => ({ ...p, [field]: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                <option value="">--</option>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
        </div>
    );
    if (type === 'select-strength') return (
        <div>
            <label className="block text-xs text-stone-400 mb-0.5">{label}{dirty && <span className="text-amber-500 ml-0.5" title="Unsaved change">●</span>}</label>
            <select value={editForm[field] || ''} onChange={e => setEditForm(p => ({ ...p, [field]: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                <option value="">--</option>
                {STRENGTHS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
        </div>
    );
    if (type === 'tags') {
        if (!editing) return (
            <div>
                <p className="text-xs text-stone-400">{label}</p>
                {Array.isArray(value) && value.length > 0 ? (
                    <div className="flex flex-wrap gap-1 mt-0.5">
                        {value.map((t, i) => (
                            <span key={`${t}-${i}`} data-testid="contact-tag-chip" className="inline-flex max-w-full [overflow-wrap:anywhere] px-1.5 py-0.5 rounded text-[10px] font-medium bg-stone-100 text-stone-600">{t}</span>
                        ))}
                    </div>
                ) : <p className="text-sm font-medium text-stone-800">--</p>}
            </div>
        );
        return (
            <div>
                <label className="block text-xs text-stone-400 mb-0.5">{label}{dirty && <span className="text-amber-500 ml-0.5" title="Unsaved change">●</span>}</label>
                <TagInput
                    value={editForm[field] || []}
                    onChange={tags => setEditForm(p => ({ ...p, [field]: tags }))}
                    placeholder="Add tags..."
                />
            </div>
        );
    }
    if (type === 'number') return (
        <div>
            <label className="block text-xs text-stone-400 mb-0.5">{label}{dirty && <span className="text-amber-500 ml-0.5" title="Unsaved change">●</span>}</label>
            <input type="number" min="0" max="100" value={editForm[field] ?? ''} onChange={e => setEditForm(p => ({ ...p, [field]: parseInt(e.target.value) || 0 }))}
                className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
        </div>
    );
    return (
        <div>
            <label className="block text-xs text-stone-400 mb-0.5">{label}{dirty && <span className="text-amber-500 ml-0.5" title="Unsaved change">●</span>}</label>
            <input type={type || 'text'} value={editForm[field] || ''} onChange={e => setEditForm(p => ({ ...p, [field]: e.target.value }))}
                data-testid={`edit-${field}-input`}
                className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
        </div>
    );
};

/**
 * CoachingCard — Bio + Do/Avoid tips. Surfaced by the Meeting Copilot
 * prep brief. Bio is a single text area; tips are per-line lists stored as
 * `tips_do: string[]` and `tips_dont: string[]`.
 */
function CoachingCard({ contact, editing, editForm, setEditForm }) {
    const doList = (editing ? editForm.tips_do : contact.tips_do) || [];
    const dontList = (editing ? editForm.tips_dont : contact.tips_dont) || [];

    const updateList = (field, value) => {
        const arr = value.split('\n').map(s => s.trim()).filter(Boolean);
        setEditForm(p => ({ ...p, [field]: arr }));
    };

    return (
        <div className="bg-white rounded-xl border border-stone-200">
            <div className="flex justify-between items-center px-4 py-3 border-b sm:px-5 border-stone-100">
                <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Coaching · Bio + Do/Avoid
                </h3>
                <span className="text-[10px] text-stone-400 uppercase tracking-widest font-bold">
                    Used by Meeting Copilot prep
                </span>
            </div>
            <div className="p-4 space-y-4 sm:p-5">
                <div>
                    <label className="block mb-1 text-xs text-stone-400">Bio</label>
                    {editing ? (
                        <textarea
                            value={editForm.bio || ''}
                            onChange={e => setEditForm(p => ({ ...p, bio: e.target.value }))}
                            rows={3}
                            placeholder="Short background + working style — 1-2 sentences the rep can scan before a call."
                            className="px-3 py-2 w-full text-sm rounded-md border border-stone-200 focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                        />
                    ) : (
                        <p className="p-3 text-sm whitespace-pre-wrap rounded-md border text-stone-700 bg-stone-50 border-stone-100">
                            {contact.bio || 'No bio yet. Click Edit and add a 1-2 sentence profile the rep can glance at before a call.'}
                        </p>
                    )}
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                        <label className="block mb-1 text-xs font-bold tracking-widest text-emerald-600 uppercase">
                            ✓ Do
                        </label>
                        {editing ? (
                            <textarea
                                value={doList.join('\n')}
                                onChange={e => updateList('tips_do', e.target.value)}
                                rows={4}
                                placeholder={'One per line — e.g.\nOpen with the outcome, not the journey\nShow ROI in the first 5 minutes'}
                                className="px-3 py-2 w-full text-sm rounded-md border border-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 bg-emerald-50/40"
                            />
                        ) : doList.length ? (
                            <ul className="p-3 space-y-1 rounded-md border border-emerald-100 bg-emerald-50/40">
                                {doList.map((t, i) => (
                                    <li key={i} className="flex gap-2 text-sm text-stone-800">
                                        <span className="flex-shrink-0 text-emerald-600">·</span>
                                        {t}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-xs italic text-stone-400">No Do tips yet.</p>
                        )}
                    </div>
                    <div>
                        <label className="block mb-1 text-xs font-bold tracking-widest text-rose-600 uppercase">
                            ✕ Avoid
                        </label>
                        {editing ? (
                            <textarea
                                value={dontList.join('\n')}
                                onChange={e => updateList('tips_dont', e.target.value)}
                                rows={4}
                                placeholder={'One per line — e.g.\nOverload with process detail\nRead from slides'}
                                className="px-3 py-2 w-full text-sm rounded-md border border-rose-200 focus:outline-none focus:ring-2 focus:ring-rose-500/20 bg-rose-50/40"
                            />
                        ) : dontList.length ? (
                            <ul className="p-3 space-y-1 rounded-md border border-rose-100 bg-rose-50/40">
                                {dontList.map((t, i) => (
                                    <li key={i} className="flex gap-2 text-sm text-stone-800">
                                        <span className="flex-shrink-0 text-rose-600">·</span>
                                        {t}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className="text-xs italic text-stone-400">No Avoid tips yet.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function LinkedInDataSection({ data }) {
    const [expanded, setExpanded] = useState(true);
    if (!data) return null;

    const experiences = data.experiences || data.positions || [];
    const skills = data.skills || [];
    const education = data.education || [];
    const summary = data.summary || data.headline || '';

    return (
        <div className="bg-white rounded-xl border border-stone-200" data-testid="linkedin-data-section">
            <button
                onClick={() => setExpanded(!expanded)}
                className="flex justify-between items-center px-4 py-3 w-full border-b sm:px-5 border-stone-100"
            >
                <div className="flex gap-2 items-center">
                    <Linkedin className="w-4 h-4 text-blue-600" />
                    <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>LinkedIn Profile</h3>
                </div>
                <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </button>
            {expanded && (
                <div className="p-4 space-y-5 sm:p-5">
                    {/* Summary */}
                    {summary && (
                        <div>
                            <h4 className="mb-2 text-xs font-semibold tracking-wider uppercase text-stone-400">Summary</h4>
                            <p className="text-sm leading-relaxed text-stone-600">{summary}</p>
                        </div>
                    )}

                    {/* Experience */}
                    {experiences.length > 0 && (
                        <div>
                            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                <Briefcase className="w-3.5 h-3.5" /> Experience
                            </h4>
                            <div className="space-y-3">
                                {experiences.map((exp, i) => (
                                    <div key={i} className="flex gap-3 pl-1">
                                        <div className="flex-shrink-0 mt-1 w-1 rounded-full bg-stone-200" style={{ minHeight: '2rem' }} />
                                        <div>
                                            <p className="text-sm font-medium text-stone-800">{exp.title || exp.role}</p>
                                            <p className="text-xs text-stone-500">{exp.company || exp.company_name}{exp.location ? ` - ${exp.location}` : ''}</p>
                                            {(exp.start_date || exp.starts_at || exp.date_range) && (
                                                <p className="text-xs text-stone-400 mt-0.5">
                                                    {exp.date_range || `${exp.start_date || exp.starts_at?.year || ''} - ${exp.end_date || exp.ends_at?.year || 'Present'}`}
                                                </p>
                                            )}
                                            {exp.description && <p className="mt-1 text-xs text-stone-500 line-clamp-2">{exp.description}</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Skills */}
                    {skills.length > 0 && (
                        <div>
                            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <Award className="w-3.5 h-3.5" /> Skills
                            </h4>
                            <div className="flex flex-wrap gap-1.5">
                                {skills.slice(0, 20).map((skill, i) => (
                                    <span key={i} className="px-2 py-1 text-xs rounded-full border bg-stone-100 text-stone-700 border-stone-200">
                                        {typeof skill === 'string' ? skill : skill.name || skill.skill}
                                    </span>
                                ))}
                                {skills.length > 20 && (
                                    <span className="px-2 py-1 text-xs text-stone-400">+{skills.length - 20} more</span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Education */}
                    {education.length > 0 && (
                        <div>
                            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <GraduationCap className="w-3.5 h-3.5" /> Education
                            </h4>
                            <div className="space-y-2">
                                {education.map((edu, i) => (
                                    <div key={i}>
                                        <p className="text-sm font-medium text-stone-800">{edu.school || edu.school_name || edu.institution}</p>
                                        <p className="text-xs text-stone-500">
                                            {edu.degree || edu.degree_name}{edu.field_of_study || edu.field ? `, ${edu.field_of_study || edu.field}` : ''}
                                        </p>
                                        {(edu.start_date || edu.starts_at || edu.date_range) && (
                                            <p className="text-xs text-stone-400">
                                                {edu.date_range || `${edu.start_date || edu.starts_at?.year || ''} - ${edu.end_date || edu.ends_at?.year || ''}`}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Contact Owner (#864) ────────────────────────────────────────────────────

/** The Contact Owner's label: the server's name, a teammate label, "Former
 *  member" for an id that no longer resolves, or null for "No Contact Owner". */
function contactOwnerLabel(ownerId, ownerName, labels) {
    if (!ownerId) return null;
    return personLabel(ownerId, ownerName || labels.get(ownerId));
}

/** A user id as conflict copy names it. */
function memberName(userId, labels) {
    if (!userId) return 'no Contact Owner';
    return labels.get(userId) || 'a former member';
}

const OWNER_EVENT_TITLES = {
    owner_assigned: 'Contact Owner changed',
    owner_restored: 'Contact Owner restored',
    coach_assigned: 'Assigned Coach changed',
    attribution_corrected: 'Referral credit corrected',
};
const OWNER_EVENT_SOURCES = {
    manual: 'Manual', bulk: 'Bulk', migration: 'Migration', restore: 'Restore', campaign: 'Campaign',
    attribution: 'Referral', api_default_owner: 'API default owner', list_management_commit: 'List Management import',
    list_management_revert: 'List Management revert', round_robin: 'Round robin', coach_manual: 'Manual',
    attribution_correction: 'Credit correction',
};
const ACTOR_KINDS = { campaign: 'Campaign', system: 'Automation', api_key: 'API key', migration: 'Migration' };
const humanize = (v) => String(v || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const sourceLabel = (source) => OWNER_EVENT_SOURCES[source] || humanize(source);
const OWNER_EVENT_TYPES = new Set(['owner_assigned', 'owner_restored']);

/** A small modal asking for a required reason (restore, and the over-quota request). */
function ReasonDialog({ title, body, confirmLabel, onConfirm, onCancel, testId }) {
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);
    useEffect(() => { inputRef.current?.focus(); }, []);
    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            await onConfirm(reason.trim());
        } catch (err) {
            setError(err?.message || 'Something went wrong. Please try again.');
            setBusy(false);
        }
    };
    return createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
             onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}>
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" role="dialog" aria-modal="true"
                 aria-labelledby={`${testId}-title`} data-testid={testId}>
                <h3 id={`${testId}-title`} className="text-lg font-bold text-stone-900 mb-2">{title}</h3>
                <p className="text-sm text-stone-600 mb-3">{body}</p>
                <label className="block text-xs font-semibold text-stone-500 mb-1" htmlFor={`${testId}-reason`}>Reason</label>
                <textarea id={`${testId}-reason`} ref={inputRef} value={reason} maxLength={500} rows={2}
                          onChange={(e) => setReason(e.target.value)} data-testid={`${testId}-reason`}
                          className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                {error && <p role="alert" className="mt-2 text-xs text-red-600">{error}</p>}
                <div className="flex justify-end gap-2 mt-4">
                    <button type="button" onClick={onCancel} className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50">Cancel</button>
                    <button type="button" onClick={submit} disabled={busy || !reason.trim()} data-testid={`${testId}-confirm`}
                            className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50">
                        {busy ? 'Working…' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}

/**
 * The Change Log's "Ownership" view (#864): the contact's committed owner,
 * coach and credit events (`GET /api/contacts/{id}/owner-events`), newest
 * first, with Restore on the latest owner event for `contacts:restore_owner`.
 * Owner changes never go into the Activities timeline.
 */
function OwnershipLog({ contactId, lastOwnerEventId, teamMembers, displayZone, getAuthHeader, canRestore, onChanged }) {
    const [events, setEvents] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [restoring, setRestoring] = useState(null);
    const failedAfter = useRef(null);  // the cursor of the page that failed, for Retry
    const labels = useMemo(() => teammateLabels(teamMembers), [teamMembers]);

    const loadSeq = useRef(0);
    const load = useCallback(async (after = null) => {
        // Only the latest request writes: a reload that overtakes a Load more (or
        // the reverse) never appends a stale page.
        const mine = ++loadSeq.current;
        setLoading(true);
        setError(null);
        failedAfter.current = after;
        try {
            const res = await axios.get(`${API}/contacts/${contactId}/owner-events`, {
                headers: getAuthHeader(), params: { limit: 50, ...(after ? { cursor: after } : {}) },
            });
            if (mine !== loadSeq.current) return;
            setEvents((prev) => (after ? [...prev, ...(res.data?.events || [])] : (res.data?.events || [])));
            setCursor(res.data?.next_cursor || null);
        } catch (err) {
            if (mine !== loadSeq.current) return;
            setError(errorText(err, "Couldn't load ownership history."));
        } finally {
            if (mine === loadSeq.current) setLoading(false);
        }
    }, [contactId, getAuthHeader]);
    // A Contact Owner change made from the profile card while this view is open
    // moves `last_owner_event_id`: reload so the new event (and its Restore) show.
    useEffect(() => { load(); }, [load, lastOwnerEventId]);

    const restoredIds = new Set(events.map((e) => e.reverts_event_id).filter(Boolean));
    const restore = async (reason) => {
        const event = restoring;
        try {
            await axios.post(`${API}/contacts/${contactId}/owner-events/${event.event_id}/restore`, { reason },
                             { headers: getAuthHeader() });
            toast.success('Contact Owner restored');
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 409 && detail?.error === 'owner_assignment_conflict') {
                toast.error(`The Contact Owner changed since this event (now: ${memberName(detail.current_owner_id, labels)}). Nothing was restored.`);
            } else if (err?.response?.status === 409
                       && ['previous_owner_inactive', 'contact_deleted'].includes(detail?.error)) {
                // #865: the previous owner left (or the contact is gone); the current owner stays.
                toast.error(errorText(err, 'The Contact Owner could not be restored.'));
            } else {
                throw new Error(errorText(err, 'The Contact Owner could not be restored.'));
            }
        }
        setRestoring(null);
        // The page reload moves `last_owner_event_id`, which reloads this list.
        if (onChanged) onChanged();
        else load();
    };
    const copy = async (id) => {
        const ok = await copyToClipboard(id);
        if (ok) toast.success('Copied');
        else toast.error('Select and copy it manually');
    };

    if (loading && !events.length) {
        return <div className="py-8 flex justify-center" role="status" data-testid="ownership-loading"><Loader2 className="w-5 h-5 animate-spin text-stone-400" /></div>;
    }
    if (error && !events.length) {
        return (
            <div className="py-8 text-center" role="alert" data-testid="ownership-error">
                <p className="text-sm text-stone-600">{error}</p>
                <button type="button" onClick={() => load()} className="mt-2 px-3 py-1.5 text-sm border border-stone-200 rounded-lg hover:bg-stone-50">Retry</button>
            </div>
        );
    }
    if (!events.length) {
        return (
            <p className="py-8 text-center text-sm text-stone-400" data-testid="ownership-empty">
                No ownership changes recorded yet. Earlier changes are under All changes.
            </p>
        );
    }
    return (
        <div data-testid="ownership-log">
            <ul className="space-y-3">
                {events.map((e) => {
                    const isOwner = OWNER_EVENT_TYPES.has(e.event_type);
                    const latest = isOwner && e.event_id === lastOwnerEventId;
                    const actor = e.actor_name || ACTOR_KINDS[e.actor_kind] || 'Someone';
                    return (
                        <li key={e.event_id} className="p-3 rounded-lg border bg-stone-50 border-stone-200 text-sm" data-testid="ownership-event">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium text-stone-800">{OWNER_EVENT_TITLES[e.event_type] || humanize(e.event_type)}</span>
                                <span className="text-xs text-stone-400">{formatDate(e.changed_at, displayZone)}</span>
                                {e.source && <span className="text-[11px] px-1.5 rounded bg-stone-200 text-stone-600" data-testid="ownership-source">{sourceLabel(e.source)}</span>}
                                <span className="flex-1" />
                                {canRestore && latest && (
                                    <button type="button" onClick={() => setRestoring(e)} data-testid="ownership-restore"
                                            className="px-2.5 py-1 text-xs border border-stone-300 rounded-md hover:bg-white">Restore</button>
                                )}
                                {isOwner && !latest && (
                                    <span className="text-xs text-stone-400" data-testid="ownership-state">{restoredIds.has(e.event_id) ? 'Restored' : 'Superseded'}</span>
                                )}
                            </div>
                            <div className="mt-1 text-stone-700">
                                {e.display?.old_label || '(none)'} → {e.display?.new_label || '(none)'}
                            </div>
                            <div className="mt-1 text-xs text-stone-500 flex flex-wrap items-center gap-1">
                                <span>by {actor}</span>
                                {e.approval_id && <span data-testid="ownership-approved">· {e.approved_by_name ? `approved by ${e.approved_by_name}` : 'approved'}</span>}
                                {e.operation_id && (
                                    <span className="inline-flex items-center gap-1">
                                        · <span className="font-mono truncate max-w-[120px]" title={e.operation_id}>{e.operation_id}</span>
                                        <button type="button" aria-label="Copy operation ID" onClick={() => copy(e.operation_id)}
                                                className="text-stone-400 hover:text-stone-700"><FileText className="w-3 h-3" /></button>
                                    </span>
                                )}
                            </div>
                            {e.reason && <p className="mt-1 text-xs text-stone-600 italic">“{e.reason}”</p>}
                        </li>
                    );
                })}
            </ul>
            {error ? (
                <div className="mt-3 flex items-center justify-center gap-2" role="alert" data-testid="ownership-more-error">
                    <p className="text-sm text-stone-600">{error}</p>
                    <button type="button" onClick={() => load(failedAfter.current)} disabled={loading}
                            className="px-3 py-1.5 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 disabled:opacity-50">Retry</button>
                </div>
            ) : cursor && (
                <div className="mt-3 flex justify-center">
                    <button type="button" onClick={() => load(cursor)} disabled={loading} data-testid="ownership-more"
                            className="px-3 py-1.5 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 disabled:opacity-50">Load more</button>
                </div>
            )}
            {restoring && (
                <ReasonDialog
                    testId="ownership-restore-dialog"
                    title="Restore the previous Contact Owner?"
                    body={`Puts back ${restoring.display?.old_label || 'no Contact Owner'} as the Contact Owner, only if nobody changed it since.`}
                    confirmLabel="Restore"
                    onConfirm={restore}
                    onCancel={() => setRestoring(null)}
                />
            )}
        </div>
    );
}

// #889 — the tabs a `?tab=` deep link may open; anything else leaves the default About tab.
const CONTACT_TAB_KEYS = ['about', 'data_insights', 'activities', 'events', 'event_photos', 'form_submissions', 'changelog', 'ai_context', 'pitchbook'];

const scrollToId = (id) => document.getElementById(id)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

// #904 — what the Open tasks strip may list: an open task that is not an Event Outreach send task
// (those keep their own card and Mark sent; a generic Complete would not record the send).
const isStripTask = (row) => row?.type === 'task' && row.task_kind !== 'outreach_send' && isTaskOpen(row);

export default function ContactDetail() {
    const { contactId } = useParams();
    const { user, getAuthHeader } = useAuth();
    const displayZone = useDisplayZone();
    const { definition: pipelineDefinition, reload: reloadPipelineDefinition } = usePipelineDefinition('sales');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isAdmin = user?.is_admin;
    // #864: owner controls gate on EFFECTIVE permissions (fail closed while
    // unknown) — managers may change a Contact Owner (D2), only admins restore (D7).
    const { hasEffectivePermission, permissionsUnknown } = usePermissions();
    const canUpdateContact = hasEffectivePermission('contacts:update');
    const canContributeActivity = hasEffectivePermission('activities:create');
    // Deals: the server's `deals:create` (POST /deals) and `deals:update` (the stakeholder routes) are separate grants.
    const canCreateDeals = hasEffectivePermission('deals:create');
    const canUpdateDeals = hasEffectivePermission('deals:update');
    // The server aliases events:read to activities:read (rbac.PERMISSION_ALIASES); /auth/permissions lists only the stored names.
    const canOpenEvents = hasEffectivePermission('events:read') || hasEffectivePermission('activities:read');
    const readOnlyReason = 'You do not have permission to update contacts.';
    const contributeReason = 'You do not have permission to add activities.';
    const canAssignOwner = hasEffectivePermission('contacts:assign_owner');
    const canRestoreOwner = hasEffectivePermission('contacts:restore_owner');
    const [contact, setContact] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [changelogView, setChangelogView] = useState('all');
    const [historyItems, setHistoryItems] = useState([]);
    const [historyCursor, setHistoryCursor] = useState(null);
    const [historyNextCursor, setHistoryNextCursor] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    // The over-quota request (#863): {body, detail, key} of the /assign that answered 429.
    const [ownerRequest, setOwnerRequest] = useState(null);
    const [account, setAccount] = useState(null);

    const [colleagues, setColleagues] = useState([]);
    // Merged activity feed — filter pills slice the loaded timeline
    // client-side. `expandedActivityId` tracks which card is open inline
    // (HubSpot-style — no modal). Right-rail rows toggle the same state
    // and switch to the Activities tab, so a click anywhere deep-links
    // to the expanded card.
    const [activityFilter, setActivityFilter] = useState('all');
    const [hideEventActivity, setHideEventActivity] = useState(false);          // #1013 (1013-106): a filter to hide them
    const [expandedActivityId, setExpandedActivityId] = useState(null);
    const [deals, setDeals] = useState([]);
    const [dealsFailed, setDealsFailed] = useState(false);
    const dealsSeq = useRef(0);
    // `fetchDeals` is called from callbacks long after the render that made it: it reads the live header.
    const authRef = useRef(getAuthHeader);
    useEffect(() => { authRef.current = getAuthHeader; });
    // The contact on screen. React Router keeps this page mounted across /contacts/:id, so a callback that awaits
    // compares it with the contact it started for before it touches the page.
    const contactIdRef = useRef(contactId);
    useEffect(() => { contactIdRef.current = contactId; });
    const [teamMembers, setTeamMembers] = useState([]); // Org users for Owner dropdown
    const [loading, setLoading] = useState(true);
    // `?tab=` picks the first tab once; the tab is not kept in sync with the URL afterwards (#889).
    const [activeTab, setActiveTab] = useState(() => {
        const requested = searchParams.get('tab');
        return CONTACT_TAB_KEYS.includes(requested) ? requested : 'about';
    });
    // An id to scroll to once the tab it lives in has rendered (the count strip's Lead and Events parts).
    const [scrollTarget, setScrollTarget] = useState(null);
    // Unified edit state for both profile and key info
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    // Set by a Save that a Required custom field blocked (D4, #972); the inline errors then follow the values.
    const [showCustomRequired, setShowCustomRequired] = useState(false);
    // Set with it: the first invalid control is focused once the About tab (where they live) has rendered.
    const [focusCustomRequired, setFocusCustomRequired] = useState(false);
    // Name of the company picked via CompanyPicker during the current edit
    // session. Lifted to the parent so the sidebar and About-tab pickers stay
    // in sync (each picker has its own internal pickedLabel otherwise).
    const [pickedAccountName, setPickedAccountName] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showRightSidebar, setShowRightSidebar] = useState(true);
    const [showEnrollModal, setShowEnrollModal] = useState(false);
    const [enrollVersion, setEnrollVersion] = useState(0);
    const [contactEnrollments, setContactEnrollments] = useState([]);
    const [showDISCEditor, setShowDISCEditor] = useState(false);
    const [enriching, setEnriching] = useState(false);
    const [showEnrichMenu, setShowEnrichMenu] = useState(false);
    const { fields: customFieldDefs } = useCustomFields('contact');

    // Sticky-header leave guard + dirty-field tracking (issue #306)
    const { isDirty, setIsDirty, blockedNavigate, showNavConfirm, confirmLeave, cancelLeave } = useUnsavedChanges(navigate);
    const { dirtyFields, dirtyCount, isFieldDirty } = useDirtyFields(editForm, contact);
    useEffect(() => { setIsDirty(editing && dirtyCount > 0); }, [editing, dirtyCount, setIsDirty]);

    // Quick log state
    const [quickLog, setQuickLog] = useState(null);
    const [showEmailComposer, setShowEmailComposer] = useState(false);
    const [showEventEnroll, setShowEventEnroll] = useState(false);
    const [eventOptions, setEventOptions] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState('');
    const [eventLoading, setEventLoading] = useState(false);
    const eventContactsApi = useMemo(() => createEventContactsApi(getAuthHeader), [getAuthHeader]);
    // #889 — the count strip, the rail card and the Events tab all read this one list of the contact's events.
    const events = useContactEvents(contactId);
    const reloadEvents = events.reload;
    // The event an Events tab card's Log call picked. The quick-log form preselects it (it is added when the
    // picker's list lacks it) and the status step sends the card's `state_version` with it.
    const pendingEventAction = useRef(null);
    const [logCallNonce, setLogCallNonce] = useState(0);
    const quickLogRef = useRef(null);
    // Events the contact is already on, to mark them in the Add to event dialog.
    const [enrolledEventIds, setEnrolledEventIds] = useState(() => new Set());

    const [logText, setLogText] = useState('');
    const [logCallDuration, setLogCallDuration] = useState('');
    const [logCallOutcome, setLogCallOutcome] = useState('completed');
    const [eventActionOptions, setEventActionOptions] = useState([]);
    const [selectedEventAction, setSelectedEventAction] = useState('');
    const [eventActionLoading, setEventActionLoading] = useState(false);
    const [logTaskTitle, setLogTaskTitle] = useState('');
    const [logTaskDue, setLogTaskDue] = useState('');
    const [logTaskOwner, setLogTaskOwner] = useState('');
    const [followUpPrompt, setFollowUpPrompt] = useState(null); // { type, eventId }
    const [eventStatusPrompt, setEventStatusPrompt] = useState(null); // { eventId, eventName, stages, activityId, channel }
    const [aiTaskSuggestions, setAiTaskSuggestions] = useState(null); // null=hidden, []=loading done
    const [aiTaskLoading, setAiTaskLoading] = useState(false);

    // A card's pending Log call belongs to one open form on one contact. Drop it when the form closes before its
    // choices load, and when the contact changes, so the next header Call does not preselect that event (and send
    // that card's `state_version`) on a different call or contact.
    useEffect(() => {
        if (quickLog !== 'call' && !showEmailComposer) pendingEventAction.current = null;
    }, [quickLog, showEmailComposer]);
    useEffect(() => () => { pendingEventAction.current = null; }, [contactId]);
    useEffect(() => {
        if (!contactId || (quickLog !== 'call' && !showEmailComposer)) return undefined;
        let cancelled = false;
        setEventActionLoading(true);
        // The picker lists only attendance-pending events, so a card's event (attended, no-show, past) is
        // added to it, and it is the preselection instead of the usual "one event, pick it" reset.
        const applyOptions = (enrolled, failed) => {
            const pending = pendingEventAction.current;
            pendingEventAction.current = null;
            if (!pending) {
                setEventActionOptions(enrolled);
                if (!failed) setSelectedEventAction(enrolled.length === 1 ? enrolled[0].event_id : '');
                return;
            }
            const listed = enrolled.some(row => row.event_id === pending.event_id);
            setEventActionOptions(listed
                ? enrolled.map(row => (row.event_id === pending.event_id ? { ...row, state_version: pending.state_version } : row))
                : [...enrolled, pending]);
            setSelectedEventAction(pending.event_id);
        };
        Promise.resolve(axios.get(`${API}/event-outreach/contacts/${contactId}/events`, {
            params: { mode: 'pre' }, headers: getAuthHeader(),
        })).then(response => {
            if (cancelled) return;
            const rows = Array.isArray(response.data?.events) ? response.data.events : [];
            applyOptions(rows.filter(row => row.attendee_status), false);
        }).catch(() => {
            if (!cancelled) applyOptions([], true);
        }).finally(() => {
            if (!cancelled) setEventActionLoading(false);
        });
        return () => { cancelled = true; };
    }, [quickLog, showEmailComposer, contactId, getAuthHeader, logCallNonce]);
    // A card's Log call can fire while the form is already open elsewhere on the page: bring it into view.
    useEffect(() => {
        if (logCallNonce > 0) quickLogRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }, [logCallNonce]);
    // The count strip's Lead and Events parts switch tab first, so the target exists only after this render.
    useEffect(() => {
        if (!scrollTarget) return;
        scrollToId(scrollTarget);
        setScrollTarget(null);
    }, [scrollTarget, activeTab]);
    useEffect(() => {
        if (!focusCustomRequired || activeTab !== 'about') return;
        const first = document.querySelector('[data-testid="custom-fields-section"] [aria-invalid="true"]');
        if (first) {
            first.scrollIntoView?.({ block: 'center' });
            first.focus?.();
        }
        setFocusCustomRequired(false);
    }, [focusCustomRequired, activeTab, showCustomRequired]);
    // A `?tab=pitchbook` link on a contact without PitchBook data would open a tab that is not offered.
    useEffect(() => {
        if (contact && activeTab === 'pitchbook' && !contact.has_pitchbook_data) setActiveTab('about');
    }, [contact, activeTab]);

    // Communications tab
    const [timeline, setTimeline] = useState([]);
    const [timelineTotal, setTimelineTotal] = useState(0);
    const [loadingTimeline, setLoadingTimeline] = useState(false);
    // #904 — every assignee's open tasks, from GET /contacts/{id}/tasks, loaded
    // with the timeline so every refresh path refreshes both.
    const [openTasks, setOpenTasks] = useState([]);
    const [openTasksTruncated, setOpenTasksTruncated] = useState(false);
    // The contact those tasks were read for. React Router keeps this page mounted across /contacts/:id, so the
    // previous contact's rows (with live Complete controls) must not show under the next one while its own read
    // is pending, or after it fails: the strip renders only rows read for the contact on screen.
    const [openTasksFor, setOpenTasksFor] = useState(contactId);
    const timelineSeq = useRef(0);
    // A pending re-read after `ncrm:tasks-changed` (see the effect below), cancelled by any explicit loadTimeline().
    const tasksEventTimer = useRef(null);
    const stripTasks = useMemo(
        () => (openTasksFor === contactId ? [...openTasks].sort(compareByDue) : []),
        [openTasks, openTasksFor, contactId],
    );
    useEffect(() => {
        setOpenTasks(prev => (prev.length ? [] : prev));
        setOpenTasksTruncated(false);
    }, [contactId]);

    // #524 — fetch-on-expand for communication-source email rows. The timeline
    // list payload carries only a bounded plain-text preview; expanding an
    // email row fetches the full document (body_html/body_text) via
    // GET /communications/{comm_id} and merges it into the shared `timeline`
    // state so BOTH the Activities feed and the right-rail Recent Activity
    // card see it. Mirrors useGmailEmailDetail's enabled-gated fetch-on-open.
    const expandedEmailRow = useMemo(() => {
        if (!expandedActivityId) return null;
        const row = timeline.find(t => (t.activity_id || t.id) === expandedActivityId);
        return isCommunicationEmailRow(row) ? row : null;
    }, [expandedActivityId, timeline]);
    const expandedEmailCommId = expandedEmailRow ? expandedEmailRow.id : null;
    // #524 — surface expand-fetch failure instead of silently falling back to
    // the plain-text preview (the plan forbids replicating loadTimeline's
    // silent error swallow). ActivityCard shows a retry affordance.
    const {
        data: expandedEmailDetail,
        isError: expandedEmailError,
        refetch: refetchExpandedEmail,
    } = useCommunicationDetail(expandedEmailCommId);
    useEffect(() => {
        if (!expandedEmailDetail || !expandedEmailCommId) return;
        // #521 residue — merge ONLY the body fields, never `...expandedEmailDetail`.
        // The detail endpoint returns the raw communications document, which
        // carries its own `source` ("crm_composer", "gmail_sync", …). Spreading
        // it overwrote the timeline row's `source: "communication"` — the very
        // discriminator `isEmailRow` keys off — so the fetched body_html was
        // merged in and then discarded at render time, leaving the expanded
        // card showing the 255-char whitespace-collapsed preview. The row is
        // the view-model and owns its own shape; the fetch supplies a body and
        // nothing else. (It also stopped injecting the doc's `created_at`,
        // which shifted the card's timestamp, and `owner_id`, which lit up an
        // Edit action that PUTs a comm_id to /activities and 404s.)
        setTimeline(prev => {
            const idx = prev.findIndex(t => (t.activity_id || t.id) === expandedEmailCommId);
            if (idx < 0) return prev;
            const patch = {
                body_html: expandedEmailDetail.body_html || '',
                body_text: expandedEmailDetail.body_text || '',
            };
            // A true no-op check, so re-running on every `timeline` change
            // cannot loop. The old guard tested `target.body_html`, which is
            // falsy forever on a text-only row: that row re-merged into a NEW
            // array on every pass, and the only thing preventing a loop was
            // `timeline` being absent from the dependency list.
            const target = prev[idx];
            if (Object.keys(patch).every(k => target[k] === patch[k])) return prev;
            const next = prev.slice();
            next[idx] = { ...target, ...patch };
            return next;
        });
        // `timeline` IS a dependency: loadTimeline() replaces the array with
        // freshly fetched rows that carry no body, and react-query serves the
        // detail from cache without changing `expandedEmailDetail`. Without
        // this the still-expanded card silently collapsed back to the preview
        // after any refresh (a save, a tab switch) with no way to recover.
    }, [expandedEmailDetail, expandedEmailCommId, timeline]);

    // Form Submissions tab
    const [submissions, setSubmissions] = useState([]);

    // New unified Record/Schedule modal
    const [showMeetingModal, setShowMeetingModal] = useState(false);
    const [meetingModalTab, setMeetingModalTab] = useState('schedule');
    // Schedule gating (issue #503): the Schedule button opens the modal only
    // when a calendar (Google, or Outlook with calendar scope) is connected.
    // The Log button stays UNGATED — logging a past meeting needs no calendar.
    const { anyCalendarConnected } = useScheduleCapabilities();
    const scheduleDisabled = !anyCalendarConnected;

    // The deals that link THIS contact, read by the server (on the deal, a stakeholder, or its company's deal;
    // the company's are tagged `contact_link: 'company'`). Filtering every org deal by account here showed nothing
    // for a company-less contact named on a deal, and every company-less deal on it. A failed read keeps the
    // card empty and tells the count strip, which shows "—" instead of a false 0. The last read started wins.
    const fetchDeals = useCallback(async () => {
        const seq = ++dealsSeq.current;
        try {
            const res = await axios.get(`${API}/deals`, {
                headers: authRef.current(), params: { contact_id: contactId, include: 'card_context' },
            });
            if (seq !== dealsSeq.current) return;
            setDeals(Array.isArray(res.data) ? res.data : (res.data?.data || []));
            setDealsFailed(false);
        } catch {
            if (seq !== dealsSeq.current) return;
            setDeals([]);
            setDealsFailed(true);
        }
    }, [contactId]);
    // A deal with no `contact_link` (an older server) counts as linked, so nothing silently drops out of the card.
    const linkedDeals = useMemo(() => deals.filter(d => d.contact_link !== 'company'), [deals]);
    const companyDeals = useMemo(() => deals.filter(d => d.contact_link === 'company'), [deals]);

    const loadData = useCallback(async () => {
        try {
            const headers = getAuthHeader();
            const [cRes, , enrollRes] = await Promise.all([
                axios.get(`${API}/contacts/${contactId}`, { headers }),
                fetchDeals(),
                axios.get(`${API}/campaigns/contact/${contactId}/progress`, { headers }).catch(() => ({ data: [] })),
            ]);
            setNotFound(false);
            setContact(cRes.data);
            setContactEnrollments(Array.isArray(enrollRes.data) ? enrollRes.data : []);
            if (cRes.data.account_id) {
                const [aRes, colRes] = await Promise.all([
                    axios.get(`${API}/accounts/${cRes.data.account_id}`, { headers }).catch(() => ({ data: null })),
                    axios.get(`${API}/contacts?account_id=${cRes.data.account_id}&page_size=50`, { headers }).catch(() => ({ data: [] })),
                ]);
                setAccount(aRes.data);
                const colData = Array.isArray(colRes.data) ? colRes.data : (colRes.data?.data || []);
                setColleagues(colData.filter(c => c.contact_id !== contactId));
            } else {
                // No linked account: clear the previous contact's account so
                // stale company data can't bleed into in-page navigation
                // (React Router keeps this page mounted across /contacts/:id).
                setAccount(null);
                setColleagues([]);
            }
        } catch (err) {
            if (err?.response?.status === 404) {
                setContact(null);
                setNotFound(true);
            } else {
                toast.error(errorText(err, 'Contact not available'));
            }
        }
        finally { setLoading(false); }
    }, [contactId, fetchDeals]);

    useEffect(() => { loadData(); }, [loadData]);

    // Load org team members for the Contact Owner dropdown (one-time)
    useEffect(() => {
        axios.get(`${API}/accounts/team-members`, { headers: getAuthHeader() })
            .then(res => setTeamMembers(Array.isArray(res.data) ? res.data : []))
            .catch(() => setTeamMembers([]));
    }, [getAuthHeader]);
    const ownerLabels = useMemo(() => teammateLabels(teamMembers), [teamMembers]);
    // The contact request can resolve after the team-members request. Do not
    // settle on the creator before the contact owner is known; that would make
    // the Task form silently assign to the creator instead of the Contact Owner.
    useEffect(() => {
        if (!contact || String(contact.contact_id) !== String(contactId)) return;
        setLogTaskOwner(contact.owner_id || user?.user_id || '');
    }, [contact, contactId, user?.user_id]);
    useEffect(() => {
        // React Router keeps this page mounted when only :contact_id changes.
        // Clear the previous contact's assignee before applying the new default.
        setLogTaskOwner('');
    }, [contactId]);

    // Load form submissions for this contact (one-time)
    useEffect(() => {
        if (!contactId) return;
        axios.get(`${API}/contacts/${contactId}/submissions`, { headers: getAuthHeader() })
            .then(res => setSubmissions(res.data?.data || []))
            .catch(() => setSubmissions([]));
    }, [contactId, getAuthHeader]);

    const startEdit = () => {
        if (!canUpdateContact) { toast.info(readOnlyReason); return; }
        setEditForm({ ...contact }); setPickedAccountName(null); setEditing(true); setIsDirty(false); setShowCustomRequired(false);
    };
    const cancelEdit = () => { setEditing(false); setEditForm({}); setPickedAccountName(null); setIsDirty(false); setShowCustomRequired(false); };

    const handleSave = async () => {
        if (!dirtyFields.length) return; // nothing to commit — no wasted write
        const missingCustom = missingRequiredCustomFields(customFieldDefs, editForm.custom_fields);
        if (Object.keys(missingCustom).length) {
            // The fields live on the About tab: Save from any other tab must take the user to them.
            setShowCustomRequired(true);
            setFocusCustomRequired(true);
            setActiveTab('about');
            const names = customFieldDefs.filter((f) => missingCustom[f.key]).map((f) => f.label || f.key);
            return toast.error(`Fill in the required custom fields: ${names.join(', ')}`);
        }
        const touchesIdentity = dirtyFields.some(k => ['first_name', 'last_name', 'email'].includes(k));
        if (touchesIdentity) {
            if (!editForm.first_name?.trim() || !editForm.last_name?.trim()) return toast.error('First and last name required');
            if (!editForm.email?.trim()) return toast.error('Email is required');
            if (!isValidEmail(editForm.email)) return toast.error('Please enter a valid email address');
        }
        setSaving(true);
        try {
            const dedicated = new Set(['pipeline_stage', 'coach_assigned_user_id', 'sales_call_outcome', 'owner_id', 'lead_status']);
            const othersDispatched = dirtyFields.some(k => k !== 'owner_id');
            const genericDirty = dirtyFields.filter(k => !dedicated.has(k));
            if (genericDirty.length > 0) {
                const draft = {};
                genericDirty.forEach(k => { draft[k] = editForm[k]; });
                await axios.put(`${API}/contacts/${contactId}`, normalizeContactPayload(draft), { headers: getAuthHeader() });
            }
            if (dirtyFields.includes('pipeline_stage')) {
                await axios.put(`${API}/contacts/${contactId}/pipeline-stage`, { stage: editForm.pipeline_stage || null }, { headers: getAuthHeader() });
            }
            if (dirtyFields.includes('coach_assigned_user_id')) {
                await axios.put(`${API}/contacts/${contactId}/assign-coach`, { coach_user_id: editForm.coach_assigned_user_id || null }, { headers: getAuthHeader() });
            }
            if (dirtyFields.includes('sales_call_outcome')) {
                await axios.put(`${API}/contacts/${contactId}/sales-outcome`, { outcome: editForm.sales_call_outcome || null }, { headers: getAuthHeader() });
            }
            if (dirtyFields.includes('lead_status')) {
                // lead_status has no editable UI yet (rendered read-only in
                // LeadPipelineCard), so this only fires if a future edit path
                // makes it dirty. The dedicated endpoint is mandatory: the
                // generic ContactUpdate model has no lead_status field (Pydantic
                // extra="ignore" would silently drop it), and /lead-status only
                // accepts the fixed enum — null (clearing) is rejected, so an
                // unset value cannot be dispatched.
                if (editForm.lead_status) {
                    await axios.put(`${API}/contacts/${contactId}/lead-status`, { status: editForm.lead_status }, { headers: getAuthHeader() });
                }
            }
            // #864: the Contact Owner goes LAST, in its own try, so a refusal can
            // say truthfully that the other edits were saved. It is version-checked
            // against the contact this edit started from (`??` keeps a stored "").
            let ownerFailure = null;
            if (canAssignOwner && dirtyFields.includes('owner_id') && editForm.owner_id) {
                const body = { owner_id: editForm.owner_id, expected_owner_id: contact.owner_id ?? null,
                               expected_ownership_version: contact.ownership_version ?? 0 };
                try {
                    await axios.put(`${API}/contacts/${contactId}/assign`, body, { headers: getAuthHeader() });
                } catch (err) {
                    ownerFailure = { err, body };
                }
            }
            await loadData();
            setEditing(false);
            setPickedAccountName(null);
            setIsDirty(false);
            setShowCustomRequired(false);
            if (ownerFailure) ownerChangeFailed(ownerFailure, othersDispatched);
            else toast.success('Contact updated');
        } catch (e) {
            try {
                const fresh = await axios.get(`${API}/contacts/${contactId}`, { headers: getAuthHeader() });
                setContact(fresh.data);
            } catch { /* keep current state */ }
            // #902: the email belongs to another contact — point at it.
            const dup = contactExists(e);
            if (dup?.id) {
                toast.error(dup.message, { action: { label: 'Open contact', onClick: () => navigate(`/contacts/${dup.id}`) } });
            } else toast.error(errorText(e, 'Failed to save'));
        } finally { setSaving(false); }
    };

    // #864: why a Contact Owner change was refused, and what happens next.
    const ownerChangeFailed = ({ err, body }, othersDispatched) => {
        const detail = err?.response?.data?.detail;
        const code = detail && typeof detail === 'object' ? detail.error : null;
        const saved = othersDispatched ? ' Your other edits were saved.' : '';
        if (err?.response?.status === 409 && code === 'owner_assignment_conflict') {
            toast.error(`Contact Owner was changed to ${memberName(detail.current_owner_id, ownerLabels)} by someone else.${saved} Reload to review the owner.`);
        } else if (err?.response?.status === 429 && code === 'owner_quota_exceeded') {
            setOwnerRequest({ body, detail, key: newIdempotencyKey() });
            if (othersDispatched) toast.success('Your other edits were saved.');
        } else {
            toast.error(`${errorText(err, 'The Contact Owner could not be changed.')}${saved}`);
        }
    };

    // The over-quota path (#863): ask a different admin to approve this one change.
    const submitOwnerRequest = async (reason) => {
        const { body, key } = ownerRequest;
        try {
            const res = await axios.post(`${API}/contacts/owner-operations/request`, {
                contact_id: contactId, new_owner_id: body.owner_id, expected_owner_id: body.expected_owner_id,
                expected_ownership_version: body.expected_ownership_version, reason, idempotency_key: key,
            }, { headers: getAuthHeader() });
            if (res.data?.status === 'noop') toast.success(`${memberName(body.owner_id, ownerLabels)} is already the Contact Owner.`);
            else toast.success('Sent for approval.');
            setOwnerRequest(null);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 409 && detail?.error === 'owner_assignment_conflict') {
                toast.error(`Contact Owner was changed to ${memberName(detail.current_owner_id, ownerLabels)} by someone else. Reload to review the owner.`);
                setOwnerRequest(null);
                loadData();
                return;
            }
            throw new Error(errorText(err, 'The request could not be sent.'));
        }
    };

    const enrichLinkedIn = async () => {
        if (!contact?.linkedin_url) return toast.error('No LinkedIn URL on this contact');
        setEnriching(true);
        setShowEnrichMenu(false);
        try {
            const res = await axios.post(`${API}/enrichment/linkedin-profile`,
                { contact_id: contactId }, { headers: getAuthHeader() });
            if (res.data.enriched) {
                toast.success('LinkedIn profile enriched!');
                loadData();
            } else {
                toast.warning(res.data.message || 'No data returned');
            }
        } catch (err) { toast.error(err.response?.data?.detail || 'Enrichment failed'); }
        finally { setEnriching(false); }
    };

    const enrichAI = async () => {
        setEnriching(true);
        setShowEnrichMenu(false);
        try {
            await axios.post(`${API}/enrichment/ai`,
                { record_type: 'contact', record_ids: [contactId] }, { headers: getAuthHeader() });
            toast.success('AI enrichment complete');
            loadData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Enrichment failed'); }
        finally { setEnriching(false); }
    };

    const enrichExa = async () => {
        setEnriching(true);
        setShowEnrichMenu(false);
        try {
            const res = await axios.post(`${API}/enrichment/exa/contact`,
                { contact_id: contactId }, { headers: getAuthHeader() });
            if (res.data.enriched) {
                toast.success('Exa enrichment complete!');
                loadData();
            } else {
                toast.warning(res.data.message || 'No results found');
            }
        } catch (err) { toast.error(err.response?.data?.detail || 'Exa enrichment failed'); }
        finally { setEnriching(false); }
    };

    // Load communications timeline and the open-tasks strip together. Overlapping
    // refreshes (a save fires one while another is in flight) resolve to the last
    // one started: a slower, older response never overwrites a newer one. `quiet`
    // re-reads without swapping the feed for its spinner (a refresh the user did not ask for).
    const loadTimeline = useCallback(async ({ quiet = false } = {}) => {
        clearTimeout(tasksEventTimer.current);   // this read covers a tasks-changed re-read that had not started
        const seq = ++timelineSeq.current;
        if (!quiet) setLoadingTimeline(true);
        try {
            const headers = getAuthHeader();
            const [timelineRes, tasksRes] = await Promise.allSettled([
                axios.get(`${API}/contacts/${contactId}/timeline`, { headers }),
                axios.get(`${API}/contacts/${contactId}/tasks`, { headers }),
            ]);
            if (seq !== timelineSeq.current) return;
            const res = timelineRes.status === 'fulfilled' ? timelineRes.value : null;
            if (res) {
                setTimeline(Array.isArray(res.data) ? res.data : (res.data?.data || []));
                // #512 audit F04 — the endpoint paginates (`page_size` defaults to
                // 50), so `timeline.length` understates the real history once a
                // contact passes that cap. `total` is the server's pre-pagination
                // count of BOTH sources; fall back to the row count for a legacy
                // plain-array response, which has no total at all.
                setTimelineTotal(Array.isArray(res.data) ? res.data.length : (res.data?.total ?? res.data?.data?.length ?? 0));
            }
            // Anything but `{data: [...]}` is an empty strip; a failed read keeps
            // what is showing rather than claiming there is nothing to do.
            if (tasksRes.status === 'fulfilled') {
                const body = tasksRes.value?.data;
                setOpenTasks(Array.isArray(body?.data) ? body.data.filter(isStripTask) : []);
                setOpenTasksTruncated(Boolean(body?.truncated));
                setOpenTasksFor(contactId);
            }
        } catch { /* timeline endpoint may not exist yet */ }
        finally { if (seq === timelineSeq.current) setLoadingTimeline(false); }
    }, [contactId, getAuthHeader]);

    // #904 — one place a changed activity reaches the page: the feed, the Recent
    // Activity rail and the open-tasks strip all render from this state.
    // `completed_at` is replaced, not merged: a reopen unsets it server-side, so
    // the response omits the key and a plain spread would keep the stale stamp.
    const applyActivityUpdate = useCallback((updated) => {
        const updatedId = updated?.activity_id || updated?.id;
        if (!updatedId) return;
        const sameRow = (row) => (row.activity_id || row.id) === updatedId;
        const merged = (row) => ({ ...row, ...updated, completed_at: updated.completed_at ?? null });
        setTimeline(prev => prev.map(row => (sameRow(row) ? merged(row) : row)));
        setOpenTasks(prev => {
            const next = merged(prev.find(sameRow) || {});
            const rest = prev.filter(row => !sameRow(row));
            if (!isStripTask(next)) return rest.length === prev.length ? prev : rest;
            return prev.some(sameRow) ? prev.map(row => (sameRow(row) ? next : row)) : [...prev, next];
        });
    }, []);

    useEffect(() => {
        if (contactId) loadTimeline();
    }, [contactId, loadTimeline]);
    // A task changed somewhere (another surface, or a 409 that left a card stale): re-read the strip and the feed
    // quietly. This page's own writes fire it too (one extra read after each), but a site that notifies and then
    // calls loadTimeline() itself costs no second read: that call cancels the pending one.
    useEffect(() => {
        if (!contactId) return undefined;
        const off = onTasksChanged(() => {
            clearTimeout(tasksEventTimer.current);
            tasksEventTimer.current = setTimeout(() => { loadTimeline({ quiet: true }); }, 0);
        });
        return () => { off(); clearTimeout(tasksEventTimer.current); };
    }, [contactId, loadTimeline]);
    useEffect(() => {
        if (activeTab !== 'changelog' || changelogView !== 'history') return;
        let cancelled = false;
        setHistoryLoading(true);
        getContactHistory(contactId, historyCursor ? { cursor: historyCursor } : {}, getAuthHeader())
            .then(data => { if (!cancelled) { const items = normalizeHistoryItems(data); setHistoryItems(prev => historyCursor ? [...prev, ...items] : items); setHistoryNextCursor(data?.next_cursor || null); } })
            .catch(err => { if (!cancelled) toast.error(errorText(err, 'Failed to load history')); })
            .finally(() => { if (!cancelled) setHistoryLoading(false); });
        return () => { cancelled = true; };
    }, [activeTab, changelogView, contactId, getAuthHeader, historyCursor]);

    // The ids of the events this contact is on, paged 100 at a time (at most 5 calls: the reverse read caps at 500).
    const loadEnrolledEventIds = async () => {
        const ids = new Set();
        let cursor = null;
        for (let page = 0; page < 5; page += 1) {
            // eslint-disable-next-line no-await-in-loop
            // Only the ids are wanted, so the server skips the follow-up, outreach and timing reads for these pages.
            const body = await eventContactsApi.listContactEvents(contactId, { limit: 100, withDetails: false, ...(cursor ? { cursor } : {}) });
            (Array.isArray(body?.events) ? body.events : []).forEach(row => { if (row?.event?.event_id) ids.add(row.event.event_id); });
            cursor = body?.next_cursor || null;
            if (!cursor) break;
        }
        return ids;
    };

    const openEventEnrollment = async () => {
        setShowEventEnroll(true);
        setEventLoading(true);
        // A pick from the last time the dialog was open may now be an event the contact is on.
        setSelectedEventId('');
        setEnrolledEventIds(new Set());
        try {
            // Marking the events the contact is already on is best effort: a failed read just leaves them unmarked.
            const [eventsResult, enrolledResult] = await Promise.allSettled([
                axios.get(`${API}/events`, { params: { status: 'published', page_size: 100 }, headers: getAuthHeader() }),
                loadEnrolledEventIds(),
            ]);
            if (eventsResult.status === 'rejected') {
                toast.error(errorText(eventsResult.reason, 'Failed to load events'));
            } else {
                const body = eventsResult.value.data;
                setEventOptions(Array.isArray(body) ? body : (body.data || []));
            }
            if (enrolledResult.status === 'fulfilled') setEnrolledEventIds(enrolledResult.value);
        } finally {
            setEventLoading(false);
        }
    };

    const enrollContactInEvent = async () => {
        if (!selectedEventId || !contactId) return;
        setEventLoading(true);
        try {
            const result = await eventContactsApi.addAttendees(selectedEventId, {
                contact_ids: [contactId], outreach: true, include_outcomes: true,
                operation_id: `contact-detail-enroll:${contactId}:${selectedEventId}`,
            });
            if (result.outcomes?.length) {
                eventEnrollmentOutcomeMessages(result.outcomes, [contact])
                    .forEach(({ tone, message }) => toast[tone](message));
            } else if (result.created || result.restored) toast.success('Contact added to event');
            else toast.info('No event membership change was needed');
            setShowEventEnroll(false);
            reloadEvents();
        } catch (err) {
            toast.error(errorText(err, 'Failed to add contact to event'));
        } finally {
            setEventLoading(false);
        }
    };


    /** The "Update status?" step for one event, with that event's stages captured NOW: the call form
     *  clears its event options when it closes, and the step used to read them afterwards (#894). */
    const promptEventStatus = (eventId, { activityId, channel } = {}) => {
        const option = eventActionOptions.find(row => row.event_id === eventId);
        setEventStatusPrompt({
            eventId, eventName: option?.name || 'Event', stages: option?.workflow?.stages || [],
            // Attended / No-show are offered only once the event has begun (#1013).
            started: eventHasStarted(option),
            // Only an Events tab card's Log call knows a version (the one the card showed); otherwise it is read at save.
            stateVersion: option?.state_version ?? null,
            activityId: activityId || null, channel,
        });
    };

    /** Events tab → Log call: opens the quick-log call form with this card's event preselected. The event's stages
     *  come from the row's own read, so the status step that follows has them for an attended or past event too. */
    const openLogCallForEvent = async (row) => {
        const eventId = row.event.event_id;
        let stages = [];
        let failure = null;
        try {
            const current = await eventContactsApi.getContact(eventId, contactId);
            stages = current?.workflow?.stages || [];
        } catch (err) { failure = err; }
        // The viewer moved to another contact while this loaded: the answer (or the failure) is for the previous
        // one, and opening its event in this contact's call form would be refused by the server on save.
        if (contactIdRef.current !== contactId) return;
        if (failure) {
            toast.error(errorText(failure, 'Could not load this event'));
            return;
        }
        pendingEventAction.current = {
            event_id: eventId, name: row.event.name, attendee_status: row.status,
            workflow: { stages }, state_version: row.state_version,
            // What the status step needs to know whether the event has begun (#1013).
            started_at: row.event.started_at, ended_at: row.event.ended_at, date: row.event.date,
            phase: row.event.phase || (row.timing?.relation === 'before' ? 'before' : row.timing?.relation ? 'event_day' : undefined),
        };
        setQuickLog('call');
        setLogCallNonce(n => n + 1);
    };

    const handleEmailSent = async ({ eventId, subject } = {}) => {
        let activityId = null;
        if (eventId) {
            try {
                activityId = (await logEventEmail({ getAuthHeader, eventId, contactId, subject }))?.activity_id || null;
            } catch (err) {
                // The email has already been sent. Surface a distinct warning so
                // the user can retry the event linkage without claiming delivery failed.
                toast.warning(errorText(err, 'Email sent, but the event follow-up could not be recorded'));
            }
            // Logging the email may have created the event's follow-up task.
            notifyTasksChanged();
            reloadEvents();
        }
        loadData();
        loadTimeline();
        if (eventId) promptEventStatus(eventId, { activityId, channel: 'email' });
    };

    // `stage` is the chosen target: a journey milestone (sent as `journey_target`, with a reason when closing) or a
    // follow-up stage of the pinned workflow (sent as `outreach_stage`), per the PATCH contract (#1013).
    const saveEventStatusPrompt = async ({ eventId: targetEventId, stage, rsvpStatus, followUpDate, closeReason }) => {
        if (!targetEventId) return;
        // "Leave unchanged" everywhere changes nothing: no write, no history entry.
        if (!stage && !rsvpStatus && !followUpDate) { setEventStatusPrompt(null); return; }
        const prompt = eventStatusPrompt || {};
        try {
            const current = await axios.get(`${API}/events/${encodeURIComponent(targetEventId)}/contacts/${encodeURIComponent(contactId)}`, { headers: getAuthHeader() });
            const attendee = current.data?.attendee || {};
            const stages = prompt.stages?.length ? prompt.stages : (current.data?.workflow?.stages || []);
            // A card's Log call sends the version its card showed, so a change made since is a 409 naming who made it.
            const version = prompt.stateVersion ?? attendee.state_version ?? 0;
            const body = {
                expected_state_version: version,
                ...statusTargetFields(stage, closeReason),
                rsvp_status: rsvpStatus || undefined,
                next_follow_up_at: followUpDate ? `${followUpDate}T23:59:59Z` : undefined,
                // The call or email this status follows is the change's evidence.
                activity_id: prompt.activityId || undefined,
                channel: prompt.channel || 'status_prompt', reason: 'update_status_prompt',
                operation_id: safeOperationId('status-prompt', targetEventId, contactId, version),
            };
            await axios.patch(`${API}/events/${encodeURIComponent(targetEventId)}/contacts/${encodeURIComponent(contactId)}`, body, { headers: getAuthHeader() });
            toast.success('Event status updated');
            setEventStatusPrompt(null);
            let closed = null;
            try {
                if (stage === CLOSE_TARGET || isFinalOutcome({ stage, rsvpStatus, stages })) {
                    closed = await offerToCloseFollowUps({ getAuthHeader, eventId: targetEventId, contactIds: [contactId] });
                }
            } finally {
                // A stage change can create or reschedule the event's follow-up task. This notifies AFTER the offer's
                // awaits: the page's quiet re-read (a tick after a notification) would otherwise fire during them,
                // and the loadTimeline() below would read the same two endpoints again.
                notifyTasksChanged();
            }
            if (closed?.failed) toast.error('Status updated, but follow-ups could not be closed');
            else if (closed?.closed) toast.success('Open follow-ups closed');
            loadData(); loadTimeline(); reloadEvents();
        } catch (err) {
            toast.error(errorText(err, 'Failed to update event status'));
            if (isEventContactConflict(err)) {
                // Someone else changed this event row first, so the cards are stale: reread them. A card's Log call
                // sent the version its card showed, which is dead now, so that step closes. A version read at save
                // time was fresh a moment ago: the step stays open with the chosen stage, and Update status again
                // reads the version afresh.
                if (prompt.stateVersion != null) setEventStatusPrompt(null);
                reloadEvents();
            }
        }
    };


    const saveQuickLog = async () => {
        try {
            const headers = getAuthHeader();
            let loggedActivity = null;
            if (quickLog === 'note') {
                if (!logText.trim()) { toast.error('Note text required'); return; }
                // NB: backend Activity model stores the body in `description`,
                // not `notes` — the Activities / timeline readers all look at
                // `description`. Sending `notes` is a silent data-loss bug.
                await axios.post(`${API}/activities`, {
                    type: 'note', title: 'Note', description: logText,
                    contact_id: contactId, account_id: contact.account_id,
                    status: 'completed',
                }, { headers });
            } else if (quickLog === 'call') {
                if (eventActionLoading) { toast.info('Loading event choices'); return; }
                if (eventActionOptions.length > 1 && !selectedEventAction) {
                    toast.error('Choose which event this call belongs to');
                    return;
                }
                loggedActivity = await axios.post(`${API}/activities`, {
                    type: 'call', title: 'Phone Call', description: logText,
                    contact_id: contactId, account_id: contact.account_id,
                    ...(selectedEventAction ? { event_id: selectedEventAction } : {}),
                    status: 'completed',
                    duration: logCallDuration ? parseInt(logCallDuration) : undefined,
                    outcome: logCallOutcome,
                }, { headers });
            } else if (quickLog === 'task') {
                if (!logTaskTitle.trim()) { toast.error('Task title required'); return; }
                await axios.post(`${API}/activities`, {
                    type: 'task', title: logTaskTitle,
                    contact_id: contactId, account_id: contact.account_id,
                    status: 'planned',
                    due_date: logTaskDue || undefined,
                    owner_id: logTaskOwner || undefined,
                }, { headers });
                notifyTasksChanged();
            }
            const loggedType = quickLog;
            const loggedEventId = selectedEventAction;
            const loggedActivityId = loggedActivity?.data?.activity_id || null;
            toast.success(`${quickLog} logged`);
            setQuickLog(null);
            setLogText('');
            setLogCallDuration('');
            setLogCallOutcome('completed');
            setSelectedEventAction('');
            setEventActionOptions([]);
            setLogTaskTitle('');
            setLogTaskDue('');
            loadData();
            loadTimeline();
            if (loggedEventId) reloadEvents();
            // Calls and emails get an explicit event status step. Notes remain
            // prompt-free (#455), and the generic task prompt is still offered
            // after the status step is dismissed.
            if ((loggedType === 'call' || loggedType === 'email') && loggedEventId) {
                promptEventStatus(loggedEventId, { activityId: loggedActivityId, channel: loggedType === 'call' ? 'phone' : 'email' });
            }
            // Follow-up prompt only after calls (#455): notes are the
            // highest-frequency action and tasks are themselves follow-ups.
            if (loggedType === 'call' && !loggedEventId) {
                setFollowUpPrompt({ type: loggedType, eventId: null });
            }
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    };

    // AI Suggest Tasks
    const fetchAiTaskSuggestions = async () => {
        setAiTaskLoading(true);
        setAiTaskSuggestions([]);
        try {
            const res = await axios.post(`${API}/ai/suggest-tasks`, {
                context_type: 'contact',
                context_id: contactId,
            }, { headers: getAuthHeader() });
            setAiTaskSuggestions(res.data.suggestions || []);
            if ((res.data.suggestions || []).length === 0) toast.info('No suggestions right now');
        } catch (err) {
            toast.error('Failed to get AI suggestions');
            setAiTaskSuggestions(null);
        } finally {
            setAiTaskLoading(false);
        }
    };

    const acceptAiTask = async (suggestion, idx) => {
        try {
            await axios.post(`${API}/activities`, {
                type: 'task',
                title: suggestion.title,
                description: suggestion.description,
                priority: suggestion.priority || 'medium',
                due_date: suggestion.due_date || undefined,
                contact_id: contactId,
                account_id: contact?.account_id,
                status: 'planned',
                source: 'ai_suggested',
            }, { headers: getAuthHeader() });
            toast.success('Task created');
            notifyTasksChanged();
            loadData();
            loadTimeline();
            setAiTaskSuggestions(prev => prev.filter((_, i) => i !== idx));
        } catch {
            toast.error('Failed to create task');
        }
    };


    // The rail card's "View all" and the count strip's Events part: open the tab, then bring the tab strip into view.
    const showEventsTab = () => { setActiveTab('events'); setScrollTarget('contact-tabs'); };

    if (loading) return <div className="flex justify-center items-center h-64"><div className="w-8 h-8 rounded-full border-4 animate-spin border-stone-700 border-t-transparent" /></div>;
    if (notFound || !contact) return (
        <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 text-center" data-testid="contact-not-found">
            <h1 className="text-xl font-semibold text-stone-900">Contact not available</h1>
            <p className="text-sm text-stone-500">This contact may have been removed or you may not have access.</p>
            <Button variant="outline" onClick={() => blockedNavigate('/contacts')}>Back to Contacts</Button>
        </div>
    );

    const changeLog = [...(contact.change_log || [])].reverse();
    const accountName = account?.name || 'No company';
    const ownerLabel = contactOwnerLabel(contact.owner_id, contact.owner_name, ownerLabels);
    const ownerChoices = teamMembers.filter(m => m.approval_status !== 'pending');

    const sharedProps = { editing, editForm, setEditForm, isFieldDirty };

    return (
        <div className="-m-6 animate-fade-in">
            <div className="flex justify-between items-center px-4 py-3 bg-white border-b sm:px-6 border-stone-200 sticky top-0 z-10">
                <div className="flex items-center min-w-0">
                    <button onClick={() => blockedNavigate('/contacts')} className="flex gap-1 items-center text-sm text-stone-500 hover:text-stone-700 shrink-0" data-testid="back-to-contacts">
                        <ChevronLeft className="w-4 h-4 mr-1" />Contacts
                    </button>
                </div>
                {editing && dirtyCount > 0 && (
                    <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full ml-2 shrink-0" data-testid="unsaved-count-badge">
                        <span className="mr-1">{dirtyCount}</span>unsaved
                    </span>
                )}
                <div className="flex gap-2 items-center">
                    {!editing && (
                        <div className="relative">
                            <Button variant="outline" size="sm" onClick={() => setShowEnrichMenu(!showEnrichMenu)} disabled={enriching} className="h-8">
                                {enriching ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                                Enrich
                            </Button>
                            {showEnrichMenu && (
                                <div className="absolute right-0 top-full z-10 py-1 mt-1 w-48 bg-white rounded-lg border shadow-lg border-stone-200">
                                    <button onClick={enrichAI} className="flex gap-2 items-center px-3 py-2 w-full text-xs text-left hover:bg-stone-50">
                                        <Sparkles className="w-3.5 h-3.5 text-amber-500" /> AI Enrich
                                    </button>
                                    <button onClick={enrichLinkedIn} className="flex gap-2 items-center px-3 py-2 w-full text-xs text-left hover:bg-stone-50">
                                        <Globe className="w-3.5 h-3.5 text-blue-500" /> LinkedIn Profile
                                    </button>
                                    <button onClick={enrichExa} className="flex gap-2 items-center px-3 py-2 w-full text-xs text-left hover:bg-stone-50">
                                        <Search className="w-3.5 h-3.5 text-indigo-500" /> Exa Search
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    {!editing && (
                        <Button size="sm" variant="outline" onClick={() => setShowEnrollModal(true)} data-testid="enroll-campaign-btn">
                            <Workflow className="w-4 h-4 mr-1" />Enroll in Campaign
                        </Button>
                    )}
                    {!editing ? (
                        <Button size="sm" variant="outline" onClick={startEdit} disabled={!canUpdateContact}
                            title={!canUpdateContact ? readOnlyReason : undefined} data-testid="inline-edit-btn">

                            <Edit3 className="w-4 h-4 mr-1" />Edit
                        </Button>
                    ) : (
                        <>
                            <Button size="sm" variant="ghost" onClick={cancelEdit} data-testid="cancel-edit-btn">
                                <X className="w-4 h-4 mr-1" />Cancel
                            </Button>
                            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-inline-btn">
                                <Check className="w-4 h-4 mr-1" />{saving ? 'Saving...' : 'Save'}
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* A deleted contact still opens (its history stays readable). A merged-away one points to
                the contact it was merged into; only the merge's Undo brings it back (#910). */}
            {contact.is_deleted && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 sm:px-6" role="status" data-testid="contact-removed-banner">
                    {contact.merged_into_contact_id ? (
                        <>
                            <span>This contact was merged into</span>
                            <button type="button" className="font-semibold underline underline-offset-2" onClick={() => blockedNavigate(`/contacts/${encodeURIComponent(contact.merged_into_contact_id)}`)} data-testid="merged-into-link">
                                {contact.merged_into_name || 'the surviving contact'}
                            </button>
                            {contact.merged_at && <span>on {formatInZone(contact.merged_at, { zone: displayZone, formatStr: 'MM/dd/yyyy hh:mm a (zzz)' })}</span>}
                            <span className="text-amber-800">· Its activities, deals, tags and events now belong to that contact. Undo the merge from Admin › Duplicate contacts › Recent merges.</span>
                        </>
                    ) : <span>This contact is deleted. Restore it from Contacts › Deleted.</span>}
                </div>
            )}

            {/* Main layout - responsive */}
            <div className="flex flex-col xl:flex-row min-h-[calc(100vh-120px)]">

                {/* ─── LEFT SIDEBAR ─── */}
                {/* #911: scroll only as the xl column; stacked below xl, overflow would clip the tag dropdown */}
                <div className="w-full bg-white border-b xl:overflow-y-auto xl:w-64 xl:flex-shrink-0 xl:border-b-0 xl:border-r border-stone-200">
                    {/* Contact Header */}
                    <div className="p-4 border-b sm:p-5 border-stone-100">
                        <div className="flex gap-4 items-center lg:flex-col lg:items-center lg:gap-0">
                            <div className="flex overflow-hidden flex-shrink-0 justify-center items-center w-16 h-16 bg-gradient-to-br rounded-full lg:w-20 lg:h-20 from-stone-100 to-stone-200 lg:mb-3" data-testid="contact-avatar">
                                {contact.avatar_url ? (
                                    <img src={contact.avatar_url} alt={`${contact.first_name} ${contact.last_name}`} className="object-cover w-full h-full" />
                                ) : (
                                    <span className="text-xl font-bold lg:text-2xl text-stone-700">{contact.first_name?.charAt(0)}{contact.last_name?.charAt(0)}</span>
                                )}
                            </div>
                            <div className="flex-1 text-left lg:text-center lg:flex-none">
                                <h1 className="text-base font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="contact-detail-name">
                                    {contact.first_name} {contact.last_name}
                                </h1>
                                <p className="text-xs text-stone-500 mt-0.5">{contact.title || '--'}</p>
                                {contact.key_role && (
                                    <span className="inline-flex px-2 py-0.5 mt-1 rounded-full text-[10px] font-medium bg-violet-50 text-violet-700">
                                        {KEY_ROLE_LABELS[contact.key_role] || contact.key_role.replace(/_/g, ' ')}
                                    </span>
                                )}
                                {account && (
                                    <button onClick={() => blockedNavigate(`/accounts/${account.account_id}`)} className="flex gap-1 items-center mt-1 text-xs text-stone-700 hover:text-stone-900 lg:mx-auto">
                                        <Building2 className="w-3 h-3" />{account.name}
                                    </button>
                                )}
                                {contact.email && (
                                    <a href={`mailto:${contact.email}`} className="flex gap-1 items-center mt-1 text-xs break-all text-stone-700 hover:underline lg:justify-center" data-testid="contact-email-link">
                                        {contact.email} <ExternalLink className="flex-shrink-0 w-3 h-3" />
                                    </a>
                                )}
                                <div className="flex items-center gap-1.5 mt-2 flex-wrap lg:justify-center">
                                    {contact.disc_profile && <DISCBadge profile={contact.disc_profile} secondary={contact.disc_secondary} size="md" />}
                                    {(contact.buying_role || contact.role_in_deal) && <span className={`text-xs px-2 py-0.5 rounded-full ${ROLE_COLORS[(contact.buying_role || contact.role_in_deal)] || 'bg-stone-100 text-stone-600'}`}>{(contact.buying_role || contact.role_in_deal)}</span>}
                                    {contact.relationship_strength && <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STRENGTH_COLORS[contact.relationship_strength] || ''}`}>{contact.relationship_strength}</span>}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tags & Contact Owner (#911) — visible at every width; Key Information below is xl-only */}
                    <div className="grid grid-cols-1 gap-3 p-4 border-b sm:grid-cols-2 xl:grid-cols-1 border-stone-100" data-testid="contact-tags-owner">
                        <p className="text-xs font-semibold tracking-wider uppercase text-stone-400 sm:col-span-2 xl:col-span-1">Tags & Contact Owner</p>
                        <InlineField label="Tags" value={contact.tags} field="tags" type="tags" {...sharedProps} />
                        {(!editing || !canAssignOwner) && (
                            <div>
                                <p className="text-xs text-stone-400">Contact Owner</p>
                                <p className="text-sm font-medium text-stone-800" data-testid="contact-owner-label">
                                    {ownerLabel || <>— <span className="ml-1 px-1.5 py-0.5 rounded bg-stone-100 text-[11px] font-normal text-stone-500">No Contact Owner</span></>}
                                </p>
                            </div>
                        )}
                        {editing && canAssignOwner && (
                            <div>
                                <label className="block text-xs text-stone-400 mb-0.5" htmlFor="contact-owner-select">Contact Owner{isFieldDirty('owner_id') && <span className="text-amber-500 ml-0.5" title="Unsaved change">●</span>}</label>
                                {/* #864: a Contact Owner can be given but never cleared (422 owner_required),
                                    and a member still pending approval is refused (owner_inactive). */}
                                <select
                                    id="contact-owner-select"
                                    data-testid="contact-owner-select"
                                    value={editForm.owner_id || ''}
                                    onChange={e => setEditForm(p => ({ ...p, owner_id: e.target.value || null }))}
                                    className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                    {!editForm.owner_id && <option value="" disabled>Choose a Contact Owner…</option>}
                                    {editForm.owner_id && !ownerChoices.some(m => m.user_id === editForm.owner_id) && (
                                        <option value={editForm.owner_id}>{ownerLabel || FORMER_MEMBER}</option>
                                    )}
                                    {ownerChoices.map(m => (
                                        <option key={m.user_id} value={m.user_id}>{ownerLabels.get(m.user_id)}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Colleagues — other contacts at the same company */}
                    {colleagues.length > 0 && (
                        <div className="p-4 border-b border-stone-100">
                            <div className="flex justify-between items-center mb-2">
                                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <Users className="w-3 h-3" /> Colleagues
                                    <span className="font-semibold text-stone-400">{colleagues.length}</span>
                                </p>
                                {account && (
                                    <button
                                        onClick={() => blockedNavigate(`/companies/${account.account_id}?tab=org-chart`)}
                                        className="text-[10px] text-stone-500 hover:text-stone-700"
                                        title="View org chart"
                                    >
                                        Org Chart →
                                    </button>
                                )}
                            </div>
                            <div className="space-y-1">
                                {colleagues.slice(0, 6).map(c => (
                                    <button
                                        key={c.contact_id}
                                        onClick={() => blockedNavigate(`/contacts/${c.contact_id}`)}
                                        className="w-full flex items-center gap-2 px-1.5 py-1 rounded-lg hover:bg-stone-50 text-left transition-colors"
                                    >
                                        <div className="flex overflow-hidden flex-shrink-0 justify-center items-center w-7 h-7 bg-gradient-to-br rounded-full from-stone-100 to-stone-200">
                                            {c.avatar_url ? (
                                                <img src={c.avatar_url} alt="" className="object-cover w-full h-full" />
                                            ) : (
                                                <span className="text-[10px] font-semibold text-stone-700">
                                                    {(c.first_name?.[0] || '?')}{(c.last_name?.[0] || '')}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-semibold truncate text-stone-800">
                                                {c.first_name} {c.last_name}
                                            </p>
                                            {c.title && (
                                                <p className="text-[10px] text-stone-500 truncate">{c.title}</p>
                                            )}
                                        </div>
                                        {c.disc_profile && (
                                            <DISCBadge profile={c.disc_profile} secondary={c.disc_secondary} size="sm" />
                                        )}
                                    </button>
                                ))}
                                {colleagues.length > 6 && account && (
                                    <button
                                        onClick={() => blockedNavigate(`/companies/${account.account_id}?tab=contacts`)}
                                        className="w-full text-[10px] text-stone-500 hover:text-stone-700 text-center py-1"
                                    >
                                        + {colleagues.length - 6} more
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Key Information - Editable */}
                    <div className="hidden p-4 xl:block">
                        <p className="mb-3 text-xs font-semibold tracking-wider uppercase text-stone-400">Key Information</p>
                        <div className="space-y-3">
                            <InlineField label="Email" value={contact.email} field="email" type="email" {...sharedProps} />
                            <InlineField label="Phone" value={contact.phone} field="phone" {...sharedProps} />
                            <InlineField label="Mobile" value={contact.mobile} field="mobile" {...sharedProps} />
                            <InlineField label="Department" value={contact.department} field="department" {...sharedProps} />
                            <InlineField label="Seniority" value={contact.seniority} field="seniority" type="select-seniority" {...sharedProps} />
                            {!editing ? (
                                <div>
                                    <p className="text-xs text-stone-400">Company</p>
                                    <p className="text-sm font-medium text-stone-800">{accountName}</p>
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-xs text-stone-400 mb-0.5">Company</label>
                                    <CompanyPicker
                                        value={editForm.account_id || ''}
                                        onChange={(id, name) => {
                                            setEditForm(p => ({ ...p, account_id: id }));
                                            setPickedAccountName(name || null);
                                        }}
                                        selectedLabel={pickedAccountName ?? account?.name}
                                        placeholder="No company"
                                        apiBase={API}
                                        getAuthHeader={getAuthHeader}
                                        testId="edit-account-select"
                                    />
                                </div>
                            )}
                            <InlineField label="Influence Score" value={contact.influence_score != null ? `${contact.influence_score}/100` : null} field="influence_score" type="number" {...sharedProps} />
                            <InlineField label="Engagement Score" value={contact.engagement_score != null ? `${contact.engagement_score}/100` : null} field="engagement_score" type="number" {...sharedProps} />
                            <InlineField label="Relationship" value={contact.relationship_strength} field="relationship_strength" type="select-strength" {...sharedProps} />
                            <InlineField label="Role in Deal" value={(contact.buying_role || contact.role_in_deal)} field="buying_role" type="select-role" {...sharedProps} />
                            <InlineField label="Source Tag" value={contact.source_tag} field="source_tag" {...sharedProps} />
                            <InlineField label="Status Event" value={contact.status_event} field="status_event" {...sharedProps} />
                            {!editing && (
                                <>
                                    <div><p className="text-xs text-stone-400">Last Updated</p><p className="text-sm font-medium text-stone-800">{formatDate(contact.updated_at, displayZone)}</p></div>
                                    <div><p className="text-xs text-stone-400">Created</p><p className="text-sm font-medium text-stone-800">{formatDate(contact.created_at, displayZone)}</p></div>
                                </>
                            )}
                        </div>
                        {contact.linkedin_url && !editing && (
                            <a href={contact.linkedin_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-stone-700 hover:underline mt-3" data-testid="linkedin-link">
                                <Linkedin className="w-3.5 h-3.5" />LinkedIn Profile
                            </a>
                        )}
                        {editing && (
                            <div className="mt-3">
                                <InlineField label="LinkedIn URL" value={contact.linkedin_url} field="linkedin_url" {...sharedProps} />
                            </div>
                        )}
                    </div>
                </div>

                {/* ─── CENTER COLUMN ─── */}
                <div className="overflow-y-auto flex-1 min-w-0 bg-stone-50">
                    {/* Quick Log Buttons */}
                    <div className="px-4 pt-3 pb-0 bg-white sm:px-6">
                        <div className="flex gap-2 items-center mb-0">
                            <button onClick={() => canContributeActivity && setQuickLog(quickLog === 'note' ? null : 'note')} disabled={!canContributeActivity}
                                title={!canContributeActivity ? contributeReason : undefined}
                                className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors ${quickLog === 'note' ? 'bg-stone-900 text-white' : 'bg-stone-100 hover:bg-stone-200 text-stone-700'}`}>
                                <FileText className="w-3 h-3" /> Note
                            </button>
                            <button onClick={() => canContributeActivity && setQuickLog(quickLog === 'call' ? null : 'call')} disabled={!canContributeActivity}
                                title={!canContributeActivity ? contributeReason : undefined}
                                className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors ${quickLog === 'call' ? 'bg-stone-900 text-white' : 'bg-stone-100 hover:bg-stone-200 text-stone-700'}`}>
                                <Phone className="w-3 h-3" /> Call
                            </button>
                            <button onClick={() => canContributeActivity && setQuickLog(quickLog === 'task' ? null : 'task')} disabled={!canContributeActivity}
                                title={!canContributeActivity ? contributeReason : undefined}
                                className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors ${quickLog === 'task' ? 'bg-stone-900 text-white' : 'bg-stone-100 hover:bg-stone-200 text-stone-700'}`}>
                                <Check className="w-3 h-3" /> Task
                            </button>
                            <button onClick={() => canContributeActivity && (() => { setMeetingModalTab('record'); setShowMeetingModal(true); })()} disabled={!canContributeActivity}
                                title={!canContributeActivity ? contributeReason : undefined}
                                className="text-xs px-3 py-1.5 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-700 flex items-center gap-1 transition-colors"
                                data-testid="record-meeting-button"
                                aria-label="Log a meeting that already happened">
                                <Calendar className="w-3 h-3" /> Log
                            </button>
                            <button onClick={() => {
                                    if (scheduleDisabled) {
                                        toast.warning(SCHEDULE_DISABLED_COPY); // intercepted click reveals why
                                        return;
                                    }
                                    setMeetingModalTab('schedule');
                                    if (canContributeActivity) setShowMeetingModal(true);
                                }}
                                aria-disabled={scheduleDisabled}
                                className={`text-xs px-3 py-1.5 rounded-lg bg-stone-100 text-stone-700 flex items-center gap-1 transition-colors ${scheduleDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-stone-200'}`}
                                data-testid="schedule-meeting-button"
                                title={scheduleDisabled ? SCHEDULE_DISABLED_COPY : 'Schedule a new meeting with invite'}>
                                <Calendar className="w-3 h-3" /> Schedule
                            </button>
                            <button onClick={() => setShowEmailComposer(true)}
                                className="text-xs px-3 py-1.5 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-700 flex items-center gap-1 transition-colors"
                                data-testid="compose-email-button"
                                title="Compose an email with templates, signatures, and AI writer">
                                <Mail className="w-3 h-3" /> Email
                            </button>
                            <div className="mx-1 w-px h-5 bg-stone-200" />
                            <button
                                onClick={() => aiTaskSuggestions !== null ? setAiTaskSuggestions(null) : fetchAiTaskSuggestions()}
                                disabled={aiTaskLoading}
                                className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors ${
                                    aiTaskSuggestions !== null
                                        ? 'bg-indigo-600 text-white'
                                        : 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200'
                                }`}
                            >
                                {aiTaskLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                AI Tasks
                            </button>
                            {/* Event Outreach — AI-drafts a 3-message × multi-channel
                                sequence tied to a specific event the contact is
                                registered for or attended. */}
                            <button
                                onClick={() => blockedNavigate(`/contacts/${contactId}/event-outreach`)}
                                data-action="contact.event_outreach"
                                title="AI Event Outreach Studio"
                                className="text-xs pl-2 pr-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors bg-violet-50 hover:bg-violet-100 text-violet-800 border border-violet-200"
                            >
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-violet-600 text-white rounded-full px-1.5 py-0.5">
                                    <Sparkles className="w-2.5 h-2.5" /> AI
                                </span>
                                <span className="font-semibold">Event Outreach</span>
                            </button>
                        </div>
                    </div>

                    {/* Quick Log Form */}
                    {quickLog && (
                        <div ref={quickLogRef} className="p-3 mx-4 mt-3 rounded-lg border sm:mx-6 bg-stone-50 border-stone-200">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-xs font-semibold capitalize text-stone-700">Log {quickLog}</span>
                                <button onClick={() => setQuickLog(null)} className="text-stone-400 hover:text-stone-600">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                            {quickLog === 'note' && (
                                <div className="flex gap-1.5">
                                    <textarea value={logText} onChange={e => setLogText(e.target.value)} rows={2}
                                        placeholder="Write a note..."
                                        className="flex-1 text-xs border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                    <VoiceButton onTranscript={t => setLogText(p => p ? `${p} ${t}` : t)} />
                                </div>
                            )}
                            {quickLog === 'call' && (
                                <div className="space-y-2">
                                    <div className="grid grid-cols-2 gap-2">
                                    {eventActionOptions.length > 0 && (
                                        <div>
                                            <label className="block text-[10px] text-stone-500 mb-0.5">Event action</label>
                                            <select value={selectedEventAction} onChange={e => setSelectedEventAction(e.target.value)} disabled={eventActionLoading || eventActionOptions.length === 1}
                                                className="w-full text-xs border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                                {eventActionOptions.length > 1 && <option value="">Choose an event…</option>}
                                                {eventActionOptions.map(row => <option key={row.event_id} value={row.event_id}>{row.name || row.title || row.event_id}</option>)}
                                            </select>
                                        </div>
                                    )}

                                        <div>
                                            <label className="block text-[10px] text-stone-500 mb-0.5">Duration (min)</label>
                                            <input type="number" value={logCallDuration} onChange={e => setLogCallDuration(e.target.value)}
                                                placeholder="0" className="w-full text-xs border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] text-stone-500 mb-0.5">Outcome</label>
                                            <select value={logCallOutcome} onChange={e => setLogCallOutcome(e.target.value)}
                                                className="w-full text-xs border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-stone-900/20">
                                                <option value="completed">Completed</option>
                                                <option value="no_answer">No Answer</option>
                                                <option value="voicemail">Voicemail</option>
                                                <option value="busy">Busy</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex gap-1.5">
                                        <textarea value={logText} onChange={e => setLogText(e.target.value)} rows={2}
                                            placeholder="Call notes..."
                                            className="flex-1 text-xs border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                        <VoiceButton onTranscript={t => setLogText(p => p ? `${p} ${t}` : t)} />
                                    </div>
                                </div>
                            )}
                            {quickLog === 'task' && (
                                <div className="space-y-2">
                                    <input value={logTaskTitle} onChange={e => setLogTaskTitle(e.target.value)}
                                        placeholder="Task title..."
                                        className="w-full text-xs border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                    <input type="date" value={logTaskDue} onChange={e => setLogTaskDue(e.target.value)}
                                        className="w-full text-xs border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                                    {teamMembers.length > 0 && <select value={logTaskOwner} onChange={e => setLogTaskOwner(e.target.value)} aria-label="Assign task to"
                                        className="w-full text-xs border border-stone-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none">
                                        {teamMembers.map(m => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
                                    </select>}
                                </div>
                            )}
                            <button onClick={saveQuickLog}
                                className="px-3 py-1 mt-2 text-xs text-white rounded-lg transition-colors bg-stone-900 hover:bg-stone-800">
                                Save
                            </button>
                        </div>
                    )}

                    {/* AI Task Suggestions */}
                    {aiTaskSuggestions !== null && (
                        <div className="p-3 mx-4 mt-3 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg border border-indigo-200 sm:mx-6">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-xs font-semibold text-indigo-700 flex items-center gap-1.5">
                                    <Sparkles className="w-3.5 h-3.5" /> AI Task Suggestions
                                </span>
                                <button onClick={() => setAiTaskSuggestions(null)} className="text-stone-400 hover:text-stone-600">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                            {aiTaskLoading ? (
                                <div className="flex justify-center items-center py-4">
                                    <Loader2 className="w-5 h-5 text-indigo-500 animate-spin" />
                                    <span className="ml-2 text-xs text-stone-500">Analyzing contact...</span>
                                </div>
                            ) : aiTaskSuggestions.length === 0 ? (
                                <p className="py-2 text-xs text-center text-stone-500">No suggestions. All caught up!</p>
                            ) : (
                                <div className="space-y-2">
                                    {aiTaskSuggestions.map((s, idx) => (
                                        <div key={idx} className="bg-white rounded-lg border border-stone-200 p-2.5 flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <span className="text-xs font-medium text-stone-900">{s.title}</span>
                                                {s.reasoning && (
                                                    <p className="text-[11px] text-stone-500 mt-0.5">{s.reasoning}</p>
                                                )}
                                                <div className="flex items-center gap-2 mt-1 text-[10px] text-stone-400">
                                                    {s.due_date && <span>{new Date(s.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                                                    {s.priority && <span className="capitalize">{s.priority}</span>}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => canContributeActivity && acceptAiTask(s, idx)} disabled={!canContributeActivity}
                                                title={!canContributeActivity ? contributeReason : undefined}
                                                className="px-2 py-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100 shrink-0"
                                            >
                                                Accept
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Tabs */}
                    <div id="contact-tabs" className="overflow-x-auto px-4 mt-0 bg-white border-b border-stone-200 scroll-mt-16 sm:px-6">
                        <div className="flex gap-0 items-center min-w-max">
                            {[
                                { key: 'about', label: 'About', icon: User },
                                { key: 'data_insights', label: 'Data Insights', icon: Database },
                                // Merged single feed with filter pills — Communications
                                // tab was redundant (both rendered the activities
                                // collection via different endpoints).
                                { key: 'activities', label: 'Activities', icon: Activity },
                                { key: 'events', label: 'Events', icon: Calendar },
                                { key: 'event_photos', label: 'Event Photos', icon: Camera },
                                { key: 'form_submissions', label: 'Form Submissions', icon: FileText },
                                { key: 'changelog', label: 'Change Log', icon: History },
                                { key: 'ai_context', label: 'AI Context', icon: Sparkles },
                                ...(contact?.has_pitchbook_data
                                    ? [{ key: 'pitchbook', label: 'PitchBook', icon: Database }] : []),
                            ].map(tab => (
                                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                                    className={`flex items-center gap-1.5 px-4 sm:px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.key ? 'border-stone-700 text-stone-700' : 'border-transparent text-stone-500 hover:text-stone-700'}`}
                                    data-testid={`tab-${tab.key}`}>
                                    <tab.icon className="w-4 h-4" />{tab.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="p-4 sm:p-6">
                        {/* ─── ABOUT TAB ─── */}
                        {activeTab === 'about' && (
                            <div className="space-y-6">
                                {/* Contact Profile */}
                                <div className="bg-white rounded-xl border border-stone-200" data-testid="contact-profile-section">
                                    <div className="flex justify-between items-center px-4 py-3 border-b sm:px-5 border-stone-100">
                                        <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>Contact Profile</h3>
                                    </div>
                                    <div className="p-4 sm:p-5">
                                        <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-2 xl:grid-cols-3">
                                            <InlineField label="First Name" value={contact.first_name} field="first_name" {...sharedProps} />
                                            <InlineField label="Last Name" value={contact.last_name} field="last_name" {...sharedProps} />
                                            <InlineField label="Email" value={contact.email} field="email" type="email" {...sharedProps} />
                                            <InlineField label="Phone" value={contact.phone} field="phone" {...sharedProps} />
                                            <InlineField label="Mobile" value={contact.mobile} field="mobile" {...sharedProps} />
                                            <InlineField label="LinkedIn URL" value={contact.linkedin_url} field="linkedin_url" {...sharedProps} />
                                            <InlineField label="Job Title" value={contact.title} field="title" {...sharedProps} />
                                            <InlineField label="Department" value={contact.department} field="department" {...sharedProps} />
                                            {!editing ? (
                                                <div><p className="text-xs text-stone-400">Company</p><p className="text-sm font-medium text-stone-800">{accountName}</p></div>
                                            ) : (
                                                <div>
                                                    <label className="block text-xs text-stone-400 mb-0.5">Company</label>
                                                    <CompanyPicker
                                                        value={editForm.account_id || ''}
                                                        onChange={(id, name) => {
                                                            setEditForm(p => ({ ...p, account_id: id }));
                                                            setPickedAccountName(name || null);
                                                        }}
                                                        selectedLabel={pickedAccountName ?? account?.name}
                                                        placeholder="No company"
                                                        apiBase={API}
                                                        getAuthHeader={getAuthHeader}
                                                        testId="edit-account-select-about"
                                                    />
                                                </div>
                                            )}
                                            <InlineField label="Seniority" value={contact.seniority} field="seniority" type="select-seniority" {...sharedProps} />
                                            <InlineField label="Role in Deal" value={(contact.buying_role || contact.role_in_deal)} field="buying_role" type="select-role" {...sharedProps} />
                                            <InlineField label="Relationship" value={contact.relationship_strength} field="relationship_strength" type="select-strength" {...sharedProps} />
                                        </div>
                                    </div>
                                    {/* Notes */}
                                    <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                                        {!editing ? (
                                            contact.notes ? (
                                                <div>
                                                    <p className="mb-1 text-xs text-stone-400">Notes</p>
                                                    <p className="p-3 text-sm whitespace-pre-wrap rounded-md border text-stone-700 bg-stone-50 border-stone-100">{contact.notes}</p>
                                                </div>
                                            ) : null
                                        ) : (
                                            <div>
                                                <label className="block text-xs text-stone-400 mb-0.5">Notes</label>
                                                <textarea value={editForm.notes || ''} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} rows={3}
                                                    data-testid="edit-notes-input"
                                                    className="w-full px-2.5 py-1.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:ring-2 focus:ring-stone-900/20 bg-white" />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Coaching — Bio + Do/Avoid tips used by the Meeting Copilot prep brief */}
                                <CoachingCard contact={contact} editing={editing} editForm={editForm} setEditForm={setEditForm} />

                                {/* Lead Pipeline + funnel fields (VSL funnel, Batch 1). The id is the count strip's Lead link (#889). */}
                                <div id="contact-lead-pipeline" className="scroll-mt-16">
                                    <LeadPipelineCard
                                        contact={contact}
                                        contactId={contactId}
                                        teamMembers={teamMembers}
                                        getAuthHeader={getAuthHeader}
                                        onChange={loadData}
                                        editing={editing}
                                        editForm={editForm}
                                        setEditForm={setEditForm}
                                        saving={saving}
                                        isFieldDirty={isFieldDirty}
                                        readOnlyReason={!canUpdateContact ? readOnlyReason : null}
                                    />
                                </div>

                                {/* DISC Personality Profile */}
                                <div className="bg-white rounded-xl border border-stone-200" data-testid="disc-profile-section">
                                    <div className="flex justify-between items-center px-4 py-3 border-b sm:px-5 border-stone-100">
                                        <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>DISC Personality Profile</h3>
                                        <Button variant="outline" size="sm" onClick={() => setShowDISCEditor(true)}>
                                            {contact.disc_profile ? 'Edit DISC' : 'Assess DISC'}
                                        </Button>
                                    </div>
                                    <div className="p-4 sm:p-5">
                                        {contact.disc_profile ? (
                                            <div className="space-y-4">
                                                <DISCBadge profile={contact.disc_profile} secondary={contact.disc_secondary} scores={contact.disc_scores} size="lg" />
                                                {contact.communication_preferences?.email_tips && (
                                                    <div>
                                                        <h4 className="mb-2 text-xs font-semibold tracking-wider uppercase text-stone-500">Communication Tips</h4>
                                                        <ul className="space-y-1">
                                                            {contact.communication_preferences.email_tips.map((tip, i) => (
                                                                <li key={i} className="flex gap-2 items-start text-sm text-stone-600">
                                                                    <span className="text-emerald-500 mt-0.5">+</span> {tip}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {contact.communication_preferences?.avoid && (
                                                    <div>
                                                        <h4 className="mb-2 text-xs font-semibold tracking-wider uppercase text-stone-500">Avoid</h4>
                                                        <ul className="space-y-1">
                                                            {contact.communication_preferences.avoid.map((tip, i) => (
                                                                <li key={i} className="flex gap-2 items-start text-sm text-stone-500">
                                                                    <span className="text-red-400 mt-0.5">-</span> {tip}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {contact.disc_notes && (
                                                    <div>
                                                        <h4 className="mb-1 text-xs font-semibold tracking-wider uppercase text-stone-500">Notes</h4>
                                                        <p className="text-sm text-stone-600">{contact.disc_notes}</p>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="py-6 text-center">
                                                <p className="text-sm text-stone-400">No DISC profile set.</p>
                                                <p className="mt-1 text-xs text-stone-400">Click "Assess DISC" to analyze communication style with AI or set manually.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Assessment Scores (partner products such as AI Agility Check) */}
                                {contact.assessments?.aiagilitycheck && (() => {
                                    const aac = contact.assessments.aiagilitycheck;
                                    const scores = aac.scores || {};
                                    const pillars = [
                                        { key: 'strategy', label: 'Strategy', color: '#6366f1' },
                                        { key: 'people', label: 'People', color: '#f59e0b' },
                                        { key: 'technology', label: 'Technology', color: '#10b981' },
                                        { key: 'execution', label: 'Execution', color: '#ef4444' },
                                    ];
                                    const total = aac.total_score || 0;
                                    const maxScore = 240;
                                    const pct = Math.round((total / maxScore) * 100);
                                    const level = pct >= 75 ? 'Advanced' : pct >= 50 ? 'Developing' : pct >= 25 ? 'Emerging' : 'Beginning';
                                    const levelColor = pct >= 75 ? 'text-emerald-600 bg-emerald-50' : pct >= 50 ? 'text-blue-600 bg-blue-50' : pct >= 25 ? 'text-amber-600 bg-amber-50' : 'text-stone-600 bg-stone-100';
                                    return (
                                        <div className="bg-white rounded-xl border border-stone-200" data-testid="assessment-scores-section">
                                            <div className="flex justify-between items-center px-4 py-3 border-b sm:px-5 border-stone-100">
                                                <div className="flex gap-2 items-center">
                                                    <div className="flex justify-center items-center w-5 h-5 bg-indigo-600 rounded">
                                                        <span className="text-white text-[10px] font-bold">AI</span>
                                                    </div>
                                                    <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Agility Check</h3>
                                                </div>
                                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${levelColor}`}>{level}</span>
                                            </div>
                                            <div className="p-4 sm:p-5">
                                                <div className="flex justify-between items-center mb-4">
                                                    <div>
                                                        <p className="text-2xl font-bold text-stone-800" style={{ fontFamily: 'Manrope, sans-serif' }}>{total}<span className="text-sm font-normal text-stone-400">/{maxScore}</span></p>
                                                        <p className="text-xs text-stone-400 mt-0.5">Overall Score</p>
                                                    </div>
                                                    <div className="text-xs text-right text-stone-400">
                                                        {aac.completed_at && <p>Completed {formatDateInZone(aac.completed_at, { zone: displayZone })}</p>}
                                                        {aac.registered_at && !aac.completed_at && <p>Registered {formatDateInZone(aac.registered_at, { zone: displayZone })}</p>}
                                                        {aac.status && <p className="capitalize mt-0.5">{aac.status}</p>}
                                                    </div>
                                                </div>
                                                <div className="space-y-3">
                                                    {pillars.map(p => {
                                                        const val = scores[p.key] || 0;
                                                        const pillarPct = Math.round((val / 60) * 100);
                                                        return (
                                                            <div key={p.key}>
                                                                <div className="flex justify-between items-center mb-1">
                                                                    <span className="text-xs font-medium text-stone-600">{p.label}</span>
                                                                    <span className="text-xs text-stone-400">{val}/60</span>
                                                                </div>
                                                                <div className="overflow-hidden w-full h-2 rounded-full bg-stone-100">
                                                                    <div className="h-full rounded-full transition-all" style={{ width: `${pillarPct}%`, backgroundColor: p.color }} />
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Custom Fields */}
                                {customFieldDefs.length > 0 && (
                                    <div className="bg-white rounded-xl border border-stone-200" data-testid="custom-fields-section">
                                        <div className="px-4 py-3 border-b sm:px-5 border-stone-100">
                                            <h3 className="flex items-center gap-2 text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                                Custom fields <CustomFieldBadge />
                                            </h3>
                                        </div>
                                        <div className="p-4 sm:p-5">
                                            <CustomFieldRenderer
                                                fields={customFieldDefs}
                                                values={editing ? (editForm.custom_fields || contact?.custom_fields || {}) : (contact?.custom_fields || {})}
                                                editing={editing}
                                                errors={editing && showCustomRequired ? missingRequiredCustomFields(customFieldDefs, editForm.custom_fields || contact?.custom_fields) : {}}
                                                onChange={(newCustomFields) => setEditForm(prev => ({ ...prev, custom_fields: newCustomFields }))}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Campaign Journey Progress */}
                                <CampaignProgressTracker key={enrollVersion} contactId={contact?.contact_id} />

                                {/* LinkedIn Data */}
                                {contact.linkedin_data && (
                                    <LinkedInDataSection data={contact.linkedin_data} />
                                )}
                            </div>
                        )}

                        {/* ─── DATA INSIGHTS TAB ─── */}
                        {activeTab === 'data_insights' && (
                            <ContactDataInsights contact={contact} />
                        )}

                        {/* ─── ACTIVITIES TAB (merged feed) ─── */}
                        {activeTab === 'activities' && (
                            <div className="p-4 bg-white rounded-xl border border-stone-200 sm:p-5">
                                <div className="flex justify-between items-center mb-3">
                                    <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>Activities</h3>
                                </div>
                                {/* #904 — open tasks of every assignee, pinned above the feed in
                                    due-date order (no date last). Outreach send tasks never
                                    appear: they keep their own card and Mark sent. */}
                                {stripTasks.length > 0 && (
                                    <div className="mb-4 rounded-lg border border-stone-200 bg-stone-50/60" data-testid="open-tasks-strip">
                                        <div className="px-3 py-2 text-xs font-semibold text-stone-600">
                                            Open tasks ({stripTasks.length}{openTasksTruncated ? '+' : ''})
                                        </div>
                                        {stripTasks.map(task => {
                                            const taskId = task.activity_id || task.id;
                                            return (
                                                <div key={taskId} className="px-3 py-2 border-t border-stone-100" data-testid="open-task-row" data-activity-id={taskId}>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-stone-800">{task.title || 'Untitled task'}</span>
                                                        <TaskStateControls activity={task} zone={displayZone} onSaved={applyActivityUpdate} compact />
                                                    </div>
                                                    <TaskAssignment activity={task} teamMembers={teamMembers} onSaved={applyActivityUpdate} compact />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                {/* Filter pills. Source: the already-merged /timeline
                                    endpoint, so adding a new activity type upstream
                                    surfaces here with no client changes. */}
                                <div className="flex items-center gap-1.5 flex-wrap mb-4 overflow-x-auto">
                                    {[
                                        { key: 'all', label: 'All activities' },
                                        { key: 'note', label: 'Notes' },
                                        { key: 'email', label: 'Emails' },
                                        { key: 'call', label: 'Calls' },
                                        { key: 'task', label: 'Tasks' },
                                        { key: 'meeting', label: 'Meetings' },
                                        // Event Outreach Studio drops task_kind='outreach_send'
                                        // on its generated send-tasks — this pill filters to them.
                                        { key: 'outreach', label: 'Outreach' },
                                        // #1013 (D11): a guest's entries from each event's activity log.
                                        { key: 'event_activity', label: 'Event activity' },
                                    ].map(pill => {
                                        const active = activityFilter === pill.key;
                                        return (
                                            <button
                                                key={pill.key}
                                                onClick={() => setActivityFilter(pill.key)}
                                                data-action="contact.filter_activities"
                                                className={`text-xs font-medium px-3 py-1 rounded-full transition-colors ${
                                                    active
                                                        ? 'text-white bg-stone-900'
                                                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                                                }`}
                                            >
                                                {pill.label}
                                            </button>
                                        );
                                    })}
                                    {activityFilter !== 'event_activity' && (
                                        <button
                                            type="button" aria-pressed={hideEventActivity} data-testid="hide-event-activity"
                                            onClick={() => setHideEventActivity(value => !value)}
                                            className={`ml-auto text-xs font-medium px-3 py-1 rounded-full border transition-colors ${hideEventActivity ? 'border-stone-900 text-stone-900 bg-white' : 'border-stone-200 text-stone-500 hover:bg-stone-50'}`}
                                        >
                                            {hideEventActivity ? 'Event activity hidden' : 'Hide event activity'}
                                        </button>
                                    )}
                                </div>
                                {(() => {
                                    const visible = hideEventActivity && activityFilter !== 'event_activity'
                                        ? timeline.filter(i => (i.type || '').toLowerCase() !== 'event_activity')
                                        : timeline;
                                    const feed = activityFilter === 'all'
                                        ? visible
                                        : activityFilter === 'outreach'
                                            ? visible.filter(i => i.task_kind === 'outreach_send')
                                            : activityFilter === 'task'
                                                ? timeline.filter(i => (i.type || '').toLowerCase() === 'task').sort(compareByDue)
                                                : timeline.filter(i => (i.type || '').toLowerCase() === activityFilter);
                                    if (loadingTimeline) {
                                        return (
                                            <div className="py-10 text-center">
                                                <Loader2 className="mx-auto w-6 h-6 animate-spin text-stone-300" />
                                            </div>
                                        );
                                    }
                                    if (feed.length === 0) {
                                        return (
                                            <div className="py-10 text-center text-stone-400">
                                                <Activity className="mx-auto mb-2 w-10 h-10 opacity-40" />
                                                <p className="text-sm">
                                                    {activityFilter === 'all'
                                                        ? 'No activities yet'
                                                        : activityFilter === 'event_activity' ? 'No event activity yet' : `No ${activityFilter}s yet`}
                                                </p>
                                                <p className="mt-1 text-xs">
                                                    Log a note, call, task, or meeting above to start building the timeline.
                                                </p>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div className="space-y-2">
                                            {feed.map((item, idx) => {
                                                const key = item.activity_id || item.id || idx;
                                                const isExpanded = expandedActivityId === key;
                                                return (
                                                    <ActivityCard
                                                        key={key}
                                                        activity={item}
                                                        expanded={isExpanded}
                                                        autoFocus={isExpanded}
                                                        emailFetchError={isExpanded && isCommunicationEmailRow(item) ? expandedEmailError : false}
                                                        onRetryEmailFetch={isExpanded && isCommunicationEmailRow(item) ? refetchExpandedEmail : undefined}
                                                        onToggle={() => setExpandedActivityId(prev => prev === key ? null : key)}
                                                        teamMembers={teamMembers}
                                                        onSaved={applyActivityUpdate}
                                                        onOpenEvent={canOpenEvents ? blockedNavigate : undefined}
                                                    />
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {/* ─── EVENTS TAB (#889) ─── */}
                        {activeTab === 'events' && (
                            <ContactEventsTab
                                events={events}
                                contactId={contactId}
                                contact={contact}
                                ownerLabel={ownerLabel}
                                zone={displayZone}
                                getAuthHeader={getAuthHeader}
                                canAct={canContributeActivity}
                                actReason={contributeReason}
                                canOpenEvent={canOpenEvents}
                                onNavigate={blockedNavigate}
                                onLogCall={openLogCallForEvent}
                                onSetFollowUp={(row) => setFollowUpPrompt({ eventId: row.event.event_id })}
                                onAddToEvent={openEventEnrollment}
                            />
                        )}

                        {/* ─── EVENT PHOTOS TAB ─── */}
                        {activeTab === 'event_photos' && (
                            <ContactEventPhotos contactId={contactId} />
                        )}

                        {/* ─── FORM SUBMISSIONS TAB ─── */}
                        {activeTab === 'form_submissions' && (
                            <div className="p-4 bg-white rounded-xl border border-stone-200 sm:p-5" data-testid="form-submissions-section">
                                <h3 className="mb-4 text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>Form Submissions</h3>
                                {submissions.length === 0 ? (
                                    <div className="py-10 text-center text-stone-400">
                                        <FileText className="mx-auto mb-2 w-10 h-10 opacity-40" />
                                        <p className="text-sm">No form submissions yet</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {submissions.map((s, idx) => {
                                            // Build a label map from field_snapshot if available
                                            const labelMap = {};
                                            (s.field_snapshot || []).forEach(f => { labelMap[f.id] = f.label; });
                                            return (
                                                <div key={s.submission_id || idx} className="p-4 rounded-lg border border-stone-200 bg-stone-50">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-semibold text-stone-800">{s.name || 'Unknown'}</span>
                                                            <span className="text-xs px-2 py-0.5 rounded-full bg-stone-200 text-stone-600">{s.source_type === 'form' ? 'Form' : s.source_type || 'Form'}</span>
                                                        </div>
                                                        <span className="text-xs text-stone-400">{s.created_at ? formatRelativeInZone(s.created_at, { zone: displayZone }) : '--'}</span>
                                                    </div>
                                                    <div className="space-y-1">
                                                        {Object.entries(s.answers || {}).map(([fid, val]) => (
                                                            <div key={fid} className="text-xs">
                                                                <span className="font-medium text-stone-600">{labelMap[fid] || fid}:</span>{' '}
                                                                <span className="text-stone-700">{String(val)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ─── CHANGELOG TAB ─── */}
                        {activeTab === 'changelog' && (
                            <div className="p-4 bg-white rounded-xl border border-stone-200 sm:p-5" data-testid="changelog-section">
                                <div className="flex items-center gap-3 mb-4 flex-wrap">
                                    <h3 className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>Change Log</h3>
                                    <span className="flex-1" />
                                    {/* #864: contributor history (owner / coach / credit events) sits beside the embedded log */}
                                    <div role="group" aria-label="Change Log view" className="inline-flex rounded-lg border border-stone-200 overflow-hidden text-xs">
                                        {[['all', 'All changes'], ['ownership', 'Ownership'], ['history', 'History']].map(([key, label]) => (
                                            <button key={key} type="button" aria-pressed={changelogView === key} onClick={() => setChangelogView(key)}
                                                    data-testid={`changelog-view-${key}`}
                                                    className={`px-3 py-1.5 ${changelogView === key ? 'bg-stone-900 text-white' : 'bg-white text-stone-600 hover:bg-stone-50'}`}>
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                {changelogView === 'history' ? (
                                    <div className="space-y-3" data-testid="contact-history-panel">
                                        {historyLoading && <p className="text-sm text-stone-400">Loading history…</p>}
                                        {!historyLoading && historyItems.length === 0 && <p className="text-sm text-stone-400">No history recorded yet.</p>}
                                        {historyItems.map((item, index) => {
                                            const changes = historyChanges(item);
                                            const operationId = historyOperationId(item);
                                            const relatedPeople = historyRelatedPeople(item);
                                            const contextLabel = historyContext(item);
                                            return <div key={item.id || index} className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm" data-testid={`contact-history-entry-${index}`}>
                                                <div className="font-medium text-stone-800">{historyKindLabel(item)}</div>
                                                <div className="text-xs text-stone-500">{actorDetails(item)} · {historyTimeLabel(item, displayZone)}</div>
                                                {item.event_name && <div className="text-xs font-medium text-stone-600">Event: {item.event_name}</div>}

                                                {(historySource(item) || operationId) && <div className="text-xs text-stone-500">{historySource(item) && `Source: ${historySourceLabel(item)}`}{historySource(item) && operationId ? ' · ' : ''}{operationId && `Operation: ${operationId}`}</div>}
                                                {contextLabel && <div className="text-xs font-medium text-stone-600">{contextLabel}</div>}
                                                {relatedPeople.length > 0 && <div className="text-xs text-stone-500">{relatedPeople.map(({ label, value }) => `${label}: ${value}`).join(' · ')}</div>}
                                                {changes.length > 0 ? <div className="mt-1 space-y-1 text-stone-600">{changes.map((change, ci) => <div key={ci}><span className="font-medium">{historyFieldLabel(change.field)}:</span> {historyValue(change.old_value)} → {historyValue(change.new_value)}</div>)}</div> : <div className="mt-1 text-stone-600">{item.message || item.description || ''}</div>}
                                            </div>;
                                        })}
                                        {historyNextCursor && <button type="button" onClick={() => setHistoryCursor(historyNextCursor)} disabled={historyLoading} className="px-3 py-1.5 text-sm border rounded-lg">Load more</button>}
                                    </div>
                                ) : changelogView === 'ownership' ? (
                                    <OwnershipLog contactId={contactId} lastOwnerEventId={contact.last_owner_event_id}
                                                  teamMembers={teamMembers} displayZone={displayZone} getAuthHeader={getAuthHeader}
                                                  canRestore={canRestoreOwner} onChanged={loadData} />
                                ) : changeLog.length === 0 ? (
                                    <div className="py-10 text-center text-stone-400">
                                        <History className="mx-auto mb-2 w-10 h-10 opacity-40" />
                                        <p className="text-sm">No changes recorded yet</p>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <div className="absolute top-0 bottom-0 left-4 w-px bg-stone-200" />
                                        <div className="space-y-4">
                                            {changeLog.map((entry, idx) => (
                                                <div key={entry.log_id || idx} className="flex relative gap-3 pl-0 sm:gap-4" data-testid={`changelog-entry-${idx}`}>
                                                    <div className="flex z-10 flex-shrink-0 justify-center items-center w-8 h-8 rounded-full border-2 bg-stone-100 border-stone-300">
                                                        <Edit3 className="w-3.5 h-3.5 text-stone-600" />
                                                    </div>
                                                    <div className="flex-1 p-3 min-w-0 rounded-lg border bg-stone-50 border-stone-200 sm:p-4">
                                                        <div className="flex flex-col gap-1 justify-between mb-2 sm:flex-row sm:items-center">
                                                            <div className="flex gap-2 items-center">
                                                                <span className="text-sm font-medium text-stone-800">{entry.user_name || 'Unknown'}</span>
                                                                <span className="text-xs text-stone-400">{entry.changes?.length || 0} change{(entry.changes?.length || 0) !== 1 ? 's' : ''}</span>
                                                                {entry.source && <span className="text-[11px] px-1.5 rounded bg-stone-200 text-stone-600" data-testid={`changelog-source-${idx}`}>{sourceLabel(entry.source)}</span>}
                                                            </div>
                                                            <span className="flex gap-1 items-center text-xs text-stone-400">
                                                                <Clock className="w-3 h-3" />{formatDate(entry.timestamp, displayZone)}
                                                            </span>
                                                        </div>
                                                        <div className="space-y-1.5">
                                                            {(entry.changes || []).map((change, ci) => (
                                                                <div key={ci} className="flex flex-wrap gap-1 items-start text-xs sm:gap-2" data-testid={`change-${idx}-${ci}`}>
                                                                    <span className="font-semibold text-stone-600 min-w-[70px] sm:min-w-[90px]">{FIELD_LABELS[change.field] || change.field}</span>
                                                                    <span className="text-red-400 line-through max-w-[140px] sm:max-w-[180px] truncate">{change.old_value || '(empty)'}</span>
                                                                    <span className="text-stone-300">&rarr;</span>
                                                                    <span className="text-emerald-600 font-medium max-w-[140px] sm:max-w-[180px] truncate">{change.new_value || '(empty)'}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ─── AI CONTEXT TAB ─── */}
                        {activeTab === 'ai_context' && (
                            <div className="p-4 bg-white rounded-xl border border-stone-200 sm:p-5">
                                <MdFilePanel type="contact" entityId={contactId} />
                            </div>
                        )}

                        {/* ─── PITCHBOOK TAB ─── */}
                        {activeTab === 'pitchbook' && (
                            <PitchBookRecordTab crmType="contact" crmId={contactId} />
                        )}
                    </div>
                </div>

                {/* ─── RIGHT SIDEBAR ─── */}
                <div className="overflow-y-auto w-full bg-white border-t xl:w-72 xl:flex-shrink-0 xl:border-t-0 xl:border-l border-stone-200">
                    {/* Count strip (#889, D6): built from reads the page already makes, no request of its own */}
                    <ContactCountStrip
                        deals={dealsFailed ? '—' : linkedDeals.length}
                        lead={contact.pipeline_stage_label || (contact.pipeline_stage ? humanizeWords(contact.pipeline_stage) : '—')}
                        events={eventsCountLabel(events)}
                        onDeals={() => scrollToId('contact-deals-card')}
                        onLead={() => { setActiveTab('about'); setScrollTarget('contact-lead-pipeline'); }}
                        onEvents={showEventsTab}
                    />

                    {/* Score Cards */}
                    <div className="p-4 border-b border-stone-100">
                        <div className="grid grid-cols-2 gap-2">
                            <div className="p-3 text-center rounded-lg bg-stone-100">
                                <p className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{contact.influence_score || 0}</p>
                                <p className="text-xs text-stone-600">Influence</p>
                            </div>
                            <div className="p-3 text-center bg-violet-50 rounded-lg">
                                <p className="text-lg font-bold text-violet-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{contact.engagement_score || 0}</p>
                                <p className="text-xs text-violet-500">Engagement</p>
                            </div>
                        </div>
                    </div>

                    {/* Attribution (#793 S6, #889): always rendered, so Original Acquisition is always labelled; it says
                        "not recorded" when no partner app credited anyone. */}
                    <ContactAttributionPanel
                        contact={contact}
                        teamMembers={teamMembers}
                        isAdmin={isAdmin}
                        getAuthHeader={getAuthHeader}
                        // The correction also writes a timeline row: refresh both.
                        onChanged={() => { loadData(); loadTimeline(); }}
                    />

                    {/* Associated Deals (#906): linked deals first, the company's own in a separate group */}
                    <ContactDealsCard
                        contact={contact}
                        account={account}
                        linkedDeals={linkedDeals}
                        companyDeals={companyDeals}
                        failed={dealsFailed}
                        definition={pipelineDefinition}
                        onStageRejected={reloadPipelineDefinition}
                        canCreate={canCreateDeals}
                        canLink={canUpdateDeals}
                        permissionsUnknown={permissionsUnknown}
                        getAuthHeader={getAuthHeader}
                        onChanged={fetchDeals}
                        onOpenDeal={(dealId) => blockedNavigate(`/deals/${dealId}`)}
                    />

                    {/* Recent Activities — rows open the same detail modal
                        as the Activities tab so note viewing / editing is
                        one-click from the rail. */}
                    <div className="p-4 border-b border-stone-100">
                        <h4 className="mb-3 text-xs font-semibold tracking-wider uppercase text-stone-400">Recent Activity ({timelineTotal})</h4>
                        {timeline.length === 0 ? (
                            <div className="py-4 text-center">
                                <Activity className="mx-auto mb-1 w-8 h-8 text-stone-200" />
                                <p className="text-xs text-stone-400">No activity</p>
                            </div>
                        ) : (
                            <div className="space-y-1">
                                {timeline.slice(0, 4).map(a => {
                                    // Communication rows (sent emails) carry `id`, not
                                    // `activity_id`. The tab keys `activity_id || id || idx`;
                                    // the rail omits the `idx` fallback because the server
                                    // always sets `id` on both row types — same effective
                                    // key, so the deep-link opens exactly the clicked card.
                                    const key = a.activity_id || a.id;
                                    return (
                                    <button
                                        type="button"
                                        key={key}
                                        onClick={() => {
                                            // Switch to the Activities tab and deep-link to
                                            // the clicked card so it auto-expands + scrolls.
                                            setActiveTab('activities');
                                            setExpandedActivityId(key);
                                        }}
                                        data-action="activity.open_detail"
                                        className="w-full text-left flex items-start gap-2 py-1.5 px-2 rounded-md hover:bg-stone-100/60 transition-colors"
                                    >
                                        <div className="w-5 h-5 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <FileText className="w-3 h-3 text-stone-400" />
                                        </div>
                                        <div className="min-w-0">
                                            {/* Same title rule as ActivityCard (#454): lead with the
                                                title, fall back to the capitalized type, and drop the
                                                secondary type label when it would just repeat it. */}
                                            <p data-testid="recent-activity-title" className="text-xs font-medium truncate text-stone-700">{activityDisplay(a).displayTitle}</p>
                                            {activityDisplay(a).hasTitle && (
                                                <p className="text-xs text-stone-400 truncate">
                                                    <span data-testid="recent-activity-type" className="capitalize">{activityDisplay(a).typeText}</span>
                                                    {/* #797: which app (API key) reported an external event. */}
                                                    {activityDisplay(a).sourceName && (
                                                        <span data-testid="recent-activity-source"> · via {activityDisplay(a).sourceName}</span>
                                                    )}
                                                </p>
                                            )}
                                        </div>
                                    </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Campaigns */}
                    <div className="p-4">
                        <h4 className="mb-3 text-xs font-semibold tracking-wider uppercase text-stone-400">Campaigns</h4>
                        {contactEnrollments.length > 0 ? (
                            <div className="space-y-2">
                                {contactEnrollments.map((enr, i) => (
                                    <div key={enr.enrollment_id || i} className="flex items-center gap-2 rounded-lg border border-stone-200 px-2.5 py-2">
                                        <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${enr.status === 'active' ? 'bg-emerald-500' : enr.status === 'paused' ? 'bg-amber-500' : enr.status === 'completed' ? 'bg-blue-500' : 'bg-stone-400'}`} />
                                        <span className="truncate text-xs font-medium text-stone-700" title={enr.campaign_name || ''}>{enr.campaign_name || 'Untitled campaign'}</span>
                                    </div>
                                ))}
                                <Button size="sm" variant="outline" className="mt-1 h-7 w-full text-xs" onClick={() => setShowEnrollModal(true)} data-testid="sidebar-enroll-campaign-btn">
                                    <Workflow className="mr-1 w-3 h-3" />Enroll in another
                                </Button>
                            </div>
                        ) : (
                            <div className="py-4 text-center">
                                <Workflow className="mx-auto mb-1 w-8 h-8 text-stone-200" />
                                <p className="text-xs text-stone-400">Enroll in a campaign</p>
                                <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={() => setShowEnrollModal(true)} data-testid="sidebar-enroll-campaign-btn">
                                    <Workflow className="mr-1 w-3 h-3" />Enroll Now
                                </Button>
                            </div>
                        )}
                    </div>
                    <ContactEventsRail
                        events={events}
                        contactId={contactId}
                        canOpenEvent={canOpenEvents}
                        zone={displayZone}
                        onNavigate={blockedNavigate}
                        onViewAll={showEventsTab}
                    />
                    <div className="border-t border-stone-100 p-4">
                        <Button size="sm" variant="outline" className="h-7 w-full text-xs" onClick={openEventEnrollment} data-testid="contact-add-to-event-btn">
                            <Calendar className="mr-1 w-3 h-3" />Add to event
                        </Button>
                    </div>
                </div>
            </div>

            {/* Add existing contact to event */}
            <Dialog open={showEventEnroll} onOpenChange={setShowEventEnroll}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader><DialogTitle>Add contact to event</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <p className="text-xs text-stone-500">The existing Contact Owner and attribution remain unchanged.</p>
                        <select value={selectedEventId} onChange={event => setSelectedEventId(event.target.value)} className="w-full rounded border border-stone-200 px-3 py-2 text-sm">
                            <option value="">— Select an event —</option>
                            {eventOptions.map(event => {
                                const already = enrolledEventIds.has(event.event_id);
                                return <option key={event.event_id} value={event.event_id} disabled={already}>{`${event.name || event.title}${already ? ' (already added)' : ''}`}</option>;
                            })}
                        </select>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setShowEventEnroll(false)}>Cancel</Button>
                            <Button onClick={enrollContactInEvent} disabled={!selectedEventId || eventLoading}>{eventLoading ? 'Adding…' : 'Add to event'}</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Enroll in Campaign Modal */}
            <EnrollCampaignModal
                isOpen={showEnrollModal}
                onClose={() => setShowEnrollModal(false)}
                contacts={contact ? [contact] : []}
                onSuccess={() => { setEnrollVersion(v => v + 1); loadData(); setShowEnrollModal(false); }}
            />


            {/* DISC Editor Modal */}
            <DISCEditor
                open={showDISCEditor}
                onOpenChange={setShowDISCEditor}
                contactId={contact?.contact_id}
                contactName={`${contact?.first_name || ''} ${contact?.last_name || ''}`.trim()}
                currentScores={contact?.disc_scores}
                onSave={(disc) => setContact(prev => ({ ...prev, ...disc }))}
            />

            {/* New unified Record/Schedule modal */}
            <MeetingActionModal
                open={showMeetingModal}
                onOpenChange={setShowMeetingModal}
                contact={contact}
                account={account}
                deal={linkedDeals[0]}
                defaultTab={meetingModalTab}
                getAuthHeader={getAuthHeader}
                readOnlyReason={!canContributeActivity ? contributeReason : null}
                onDone={() => { loadData(); loadTimeline(); }}
            />

            {/* Email composer (templates + signatures + AI writer with contact MD context) */}
            <ContactEmailComposer
                open={showEmailComposer}
                onOpenChange={setShowEmailComposer}
                contact={contact}
                account={account}
                deal={linkedDeals[0]}
                user={user}
                getAuthHeader={getAuthHeader}
                eventOptions={eventActionOptions}
                onSent={handleEmailSent}
            />


            <EventStatusPrompt
                open={!!eventStatusPrompt}
                event={eventStatusPrompt}
                eventOptions={eventActionOptions}
                onClose={() => setEventStatusPrompt(null)}
                onSave={saveEventStatusPrompt}
            />

            {/* Follow-Up Prompt */}
            <FollowUpPrompt
                open={!!followUpPrompt}
                eventId={followUpPrompt?.eventId}
                onClose={() => setFollowUpPrompt(null)}
                contactId={contactId}
                contactOwnerId={contact?.owner_id}
                teamMembers={teamMembers}
                contactName={contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : ''}
                accountId={contact?.account_id}
                onCreated={() => { loadData(); loadTimeline(); if (followUpPrompt?.eventId) reloadEvents(); }}
            />

            {/* #864: over the daily limit → ask a different admin to approve this change */}
            {ownerRequest && (
                <ReasonDialog
                    testId="owner-request-dialog"
                    title="Request approval"
                    body={`You've used today's Contact Owner changes. The limit resets ${formatInZone(ownerRequest.detail?.resets_at, { zone: ownerRequest.detail?.zone })} (${ownerRequest.detail?.zone || 'UTC'}). Request approval from an admin to make ${memberName(ownerRequest.body.owner_id, ownerLabels)} the Contact Owner?`}
                    confirmLabel="Request approval"
                    onConfirm={submitOwnerRequest}
                    onCancel={() => setOwnerRequest(null)}
                />
            )}

            {/* Leave-confirm modal — unsaved changes guard (issue #306) */}
            {showNavConfirm && createPortal(
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="leave-confirm-modal">
                    <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6">
                        <h3 className="text-lg font-bold text-stone-900 mb-2">Unsaved Changes</h3>
                        <p className="text-sm text-stone-500 mb-4">You have unsaved changes. Are you sure you want to leave?</p>
                        <div className="flex justify-end gap-2">
                            <button onClick={cancelLeave} className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50" data-testid="leave-stay-btn">Stay</button>
                            <button onClick={confirmLeave} className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600" data-testid="leave-leave-btn">Leave</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}


function EventStatusPrompt({ open, event, eventOptions, onClose, onSave }) {
    const [stage, setStage] = useState('');
    const [closeReason, setCloseReason] = useState(CLOSE_REASONS[0].key);
    const [rsvpStatus, setRsvpStatus] = useState('');
    const [followUpDate, setFollowUpDate] = useState('');
    // The stages captured when the prompt opened; the live options are only a fallback.
    const selected = eventOptions.find(option => option.event_id === event?.eventId);
    const stages = event?.stages?.length ? event.stages : (selected?.workflow?.stages || []);
    // To invite … Going, then the follow-up stages, then Closed; Attended and No-show once the event has begun (#1013).
    const targets = statusTargetOptions({ stages, started: Boolean(event?.started ?? eventHasStarted(selected)) });
    useEffect(() => {
        if (open) { setStage(''); setCloseReason(CLOSE_REASONS[0].key); setRsvpStatus(''); setFollowUpDate(''); }
    }, [open, event?.eventId]);
    if (!open) return null;
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent data-testid="event-status-prompt">
                <DialogHeader><DialogTitle>Update status?</DialogTitle></DialogHeader>
                <p className="text-sm text-stone-500">Update the status for {event?.eventName || 'this event'}.</p>
                <label className="block text-xs font-medium text-stone-600">Stage
                    <select value={stage} onChange={e => setStage(e.target.value)} className="mt-1 w-full rounded border border-stone-200 px-2 py-2 text-sm">
                        <option value="">Leave unchanged</option>
                        {targets.map(option => <option key={option.key} value={option.key}>{option.label}</option>)}
                    </select>
                </label>
                {stage === CLOSE_TARGET && (
                    <label className="block text-xs font-medium text-stone-600">Reason
                        <select value={closeReason} onChange={e => setCloseReason(e.target.value)} className="mt-1 w-full rounded border border-stone-200 px-2 py-2 text-sm" data-testid="event-status-close-reason">
                            {CLOSE_REASONS.map(option => <option key={option.key} value={option.key}>{option.label}</option>)}
                        </select>
                    </label>
                )}
                <label className="block text-xs font-medium text-stone-600">RSVP (optional)
                    <select value={rsvpStatus} onChange={e => setRsvpStatus(e.target.value)} className="mt-1 w-full rounded border border-stone-200 px-2 py-2 text-sm">
                        <option value="">Leave unchanged</option><option value="confirmed">Confirmed</option><option value="declined">Declined</option><option value="cancelled">Cancelled</option>
                    </select>
                </label>
                <label className="block text-xs font-medium text-stone-600">Follow up date (optional)
                    <input type="date" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)} className="mt-1 w-full rounded border border-stone-200 px-2 py-2 text-sm" />
                </label>
                <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Skip</Button><Button onClick={() => onSave({ eventId: event.eventId, stage, rsvpStatus, followUpDate, closeReason })}>Update status</Button></div>
            </DialogContent>
        </Dialog>
    );
}
